import React, { useState, useMemo, useEffect, useRef } from 'react';
import { API_BASE_URL, AUTHORIZED_ROLES } from '../constants/property';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Grid from '../components/Grid';
import Pagination from '../components/Pagination';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { plantNames } from '../constants/property';
import { Snackbar, Alert } from '@mui/material';
import { formatIndianGstNumber, isValidIndianGstNumber } from '../utils/gst';
import {
  parseRecordDateTime,
  compareRecordsNewestFirst,
  defaultSortDirectionForColumn,
} from '../utils/tableSort';

const ModalSection = ({ children }) => (
  <section className="mb-6 last:mb-0">
    {children}
  </section>
);

const AmountWithGstHint = ({ gstPercentage, withGST }) => {
  const pct = String(gstPercentage ?? '').trim().replace(/%$/, '').replace(/,/g, '');
  if (!pct || !Number.isFinite(parseFloat(pct)) || !withGST) return null;
  return (
    <p className="mt-1 text-xs text-gray-500">Amount with GST: {withGST}</p>
  );
};

const StockIn = () => {
  const location = useLocation();
  const { hasAnyRole } = useAuth();
  const isAdministrator = hasAnyRole(AUTHORIZED_ROLES);
  const [selectedOption, setSelectedOption] = useState('plant');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  // Report filters
  const [datePreset, setDatePreset] = useState(''); // today|yesterday|last7|last30|thisMonth|lastMonth|custom
  const [dateDisplay, setDateDisplay] = useState('');
  const [showDateDropdown, setShowDateDropdown] = useState(false);
  const [showCustomCalendar, setShowCustomCalendar] = useState(false);
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedStore, setSelectedStore] = useState('all');
  const [selectedProduct, setSelectedProduct] = useState('all');
  const [selectedVendor, setSelectedVendor] = useState('all');
  const [selectedSupervisor, setSelectedSupervisor] = useState('all');
  const [storeInputValue, setStoreInputValue] = useState('');
  const [showStoreDropdown, setShowStoreDropdown] = useState(false);
  const [productInputValue, setProductInputValue] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [vendorInputValue, setVendorInputValue] = useState('');
  const [showVendorDropdown, setShowVendorDropdown] = useState(false);
  const [supervisorInputValue, setSupervisorInputValue] = useState('');
  const [showSupervisorDropdown, setShowSupervisorDropdown] = useState(false);
  const [supervisorsFromApi, setSupervisorsFromApi] = useState([]);
  const [maintenanceVendorOptions, setMaintenanceVendorOptions] = useState([]);
  const [plantMaterialsFromApi, setPlantMaterialsFromApi] = useState([]);
  const [siteMaterialsFromApi, setSiteMaterialsFromApi] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [maintenancePlantOptions, setMaintenancePlantOptions] = useState(() => {
    const src = plantNames?.length ? plantNames : [{ name: 'Plant', value: 'Plant', label: 'Plant' }];
    return src.map((p) => ({ value: p.value, label: p.label || p.value }));
  });
  const defaultPlant = maintenancePlantOptions[0]?.value ?? 'Plant';

  // Populated from GET /api/maintence/Site (maintenance master)
  const [maintenanceSiteOptions, setMaintenanceSiteOptions] = useState(() => [
    { value: 'st1998', label: 'ST1998' },
    { value: 'st2765', label: 'ST2765' },
    { value: 'st3865', label: 'ST3865' },
    { value: 'st4765', label: 'ST4765' },
    { value: 'st19098', label: 'ST19098' },
    { value: 'st2003', label: 'ST2003' },
  ]);
  const siteCodes = maintenanceSiteOptions;

  // Helper function to get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  const [formData, setFormData] = useState({
    date: getTodayDate(),
    materialtype: '',
    customMaterialType: '',
    grade: '',
    qty: '',
    qtyUnit: '',
    sqmm: '',
    vendor: '',
    totalamount: '',
    withGST: '',
    supervisor: '',
    sitecode: '',
    dc: '',
    grossWeight: '',
    tareWeight: '',
    netWeight: '',
    tonnageCFT: '',
    rate: '',
    quantity: '',
    cftTotalAmount: '',
    plant: defaultPlant,
    gstNumber: '',
    gstPercentage: '',
    invoiceNumber: ''
  });
  const [modalSelectedOption, setModalSelectedOption] = useState('plant');
  const [showFilters, setShowFilters] = useState(true);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [sortColumn, setSortColumn] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [showColumnMenu, setShowColumnMenu] = useState(false);
  const defaultVisibleColumns = {
    date: true,
    sitecode: true,
    plant: true,
    dc: true,
    materialtype: true,
    grade: true,
    grossWeight: true,
    tareWeight: true,
    netWeight: true,
    quantity: true,
    rate: true,
    weight: true,
    qty: true,
    vendor: true,
    totalamount: true,
    withGST: true,
    supervisor: true,
    invoiceNumber: true,
    gstNumber: true,
    gstPercentage: true,
    actions: true
  };
  const [visibleColumns, setVisibleColumns] = useState(defaultVisibleColumns);

  // Ensure newly added columns (e.g. plant) are visible when defaults are updated
  useEffect(() => {
    setVisibleColumns((prev) => ({ ...defaultVisibleColumns, ...prev, plant: true }));
  }, []);

  // Check if modal should be opened from navigation
  useEffect(() => {
    if (location.state?.openModal) {
      setIsModalOpen(true);
      // Clear the state to prevent reopening on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Fetch supervisors (role = Supervisor) from employee API
  useEffect(() => {
    const fetchSupervisors = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/employee`, {
          method: 'GET',
          mode: 'cors'
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        const options = (Array.isArray(data) ? data : [])
          .filter(emp => emp.role === 'Supervisor')
          .map(emp => {
            const label =
              emp.userName ||
              emp.employeeName ||
              `${emp.firstName || ''} ${emp.lastName || ''}`.trim();
            return label ? { value: label, label } : null;
          })
          .filter(Boolean);

        setSupervisorsFromApi(options);
      } catch (err) {
        console.error('Error fetching supervisors from employee API:', err);
      }
    };

    fetchSupervisors();
  }, []);

  // Fetch plant and site material types from the Material Type maintenance API
  useEffect(() => {
    const fetchMaterialTypes = async () => {
      try {
        const [plantRes, siteRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/material/bytype?type=stockInPlant&category=%22%22`, { method: 'GET', mode: 'cors' }),
          fetch(`${API_BASE_URL}/api/material/bytype?type=stockInSite&category=%22%22`, { method: 'GET', mode: 'cors' }),
        ]);
        if (plantRes.ok) {
          const data = await plantRes.json();
          const list = Array.isArray(data) ? data : (Array.isArray(data?.materials) ? data.materials : []);
          if (list.length) setPlantMaterialsFromApi(list);
        }
        if (siteRes.ok) {
          const data = await siteRes.json();
          const list = Array.isArray(data) ? data : (Array.isArray(data?.materials) ? data.materials : []);
          if (list.length) setSiteMaterialsFromApi(list);
        }
      } catch (err) {
        console.error('Error fetching material types for StockIn:', err);
      }
    };
    fetchMaterialTypes();
  }, []);

  /**
   * Legacy API rows may store GST % as a numeric gstNumber/gstNo, or a GSTIN string.
   * Prefer explicit gstPercentage from API when present.
   */
  const splitGstFromApi = (item) => {
    const apiPct =
      item.gstPercentage != null && item.gstPercentage !== ''
        ? String(item.gstPercentage).replace(/%$/, '').trim()
        : '';
    const raw = String(item.gstNumber || item.gstNo || '').trim();
    if (apiPct) {
      return {
        gstNumber: raw && !/^\d+(\.\d+)?$/.test(raw) ? formatIndianGstNumber(raw) : '',
        gstPercentage: apiPct,
      };
    }
    if (/^\d+(\.\d+)?$/.test(raw)) {
      return { gstNumber: '', gstPercentage: raw };
    }
    if (raw) {
      return { gstNumber: formatIndianGstNumber(raw), gstPercentage: '' };
    }
    return { gstNumber: '', gstPercentage: '' };
  };

  const parseStockInGstPercent = (raw) => {
    const s = String(raw ?? '').trim().replace(/%$/, '').replace(/,/g, '');
    if (!s) return null;
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  };

  const computeAmountWithGST = (amount, gstPercentageStr) => {
    const a = parseFloat(amount);
    if (!Number.isFinite(a) || a <= 0) return '';
    const pct = parseStockInGstPercent(gstPercentageStr);
    if (pct == null) return '';
    return (a * (1 + pct / 100)).toFixed(2);
  };

  /** Returns the full API material object for the given material type name, or null. */
  const getApiMaterialData = (materialTypeName, modalOption) => {
    if (!materialTypeName) return null;
    const list = modalOption === 'plant' ? plantMaterialsFromApi : siteMaterialsFromApi;
    const lc = String(materialTypeName).toLowerCase().trim();
    return list.find((m) => String(m.materialType ?? m.materialtype ?? '').toLowerCase().trim() === lc) ?? null;
  };

  /** Case-insensitive check: does the material type match any of the given names? */
  const matchMat = (value, ...names) => {
    const lc = String(value ?? '').toLowerCase().trim();
    return names.some((n) => lc === String(n).toLowerCase().trim());
  };

  /** Extracts the unit strings from an API material row (handles all field/type variants). */
  const extractApiUnits = (row) => {
    if (!row) return [];
    const raw = Array.isArray(row?.units)
      ? row.units
      : (Array.isArray(row?.materialUnit)
        ? row.materialUnit
        : (Array.isArray(row?.materialunit) ? row.materialunit : []));
    return raw
      .map((u) => (typeof u === 'string' ? u : (u?.unitName ?? u?.name ?? '')))
      .map((x) => String(x).trim())
      .filter(Boolean);
  };

  /** Extracts the grade strings from an API material row (handles all field/type variants). */
  const extractApiGrades = (row) => {
    if (!row) return [];
    const raw = Array.isArray(row?.grades)
      ? row.grades
      : (Array.isArray(row?.materialGrade)
        ? row.materialGrade
        : (Array.isArray(row?.materialgrade) ? row.materialgrade : []));
    return raw
      .map((g) => (typeof g === 'string' ? g : (g?.grade ?? g?.name ?? '')))
      .map((x) => String(x).trim())
      .filter(Boolean);
  };

  /** Returns materialUnit array for the current material from API, or empty array. */
  const getApiUnits = (materialTypeName, modalOption) => {
    const mat = getApiMaterialData(materialTypeName, modalOption);
    return extractApiUnits(mat);
  };

  /** Table + modal Quantity: strip CFT / metric tons (those belong in weight/unit columns or materialUnit). */
  const quantityDisplayNoWeightUnit = (raw) => {
    if (raw == null || raw === '') return '';
    const s = String(raw).trim();
    if (!s || s === '-') return '';
    if (/\bmetric\s*tons?\b/i.test(s) || /\bcft\b/i.test(s) || /\bbag\b/i.test(s)) {
      const n = parseFloat(s.replace(/,/g, ''));
      return Number.isFinite(n) ? String(n) : '';
    }
    return s;
  };

  /** Quantity column / exports: show exactly 3 decimals when value is a plain number (avoid mangling "10 Meters"). */
  const formatQuantityColumn3 = (raw) => {
    if (raw == null || raw === '') return '';
    const s = String(raw).trim();
    if (!s || s === '-') return '';
    const normalized = s.replace(/,/g, '');
    if (!/^-?\d+(?:\.\d+)?$/.test(normalized)) return s;
    const n = parseFloat(normalized);
    return Number.isFinite(n) ? n.toFixed(3) : s;
  };

  // Helper function to transform API data to component structure
  const transformApiData = (apiData, isPlant = true) => {
    const records = [];
    let recordId = 1;

    const formatWeight3 = (val) => {
      if (val === null || val === undefined || val === '') return '';
      const n = parseFloat(val);
      if (Number.isNaN(n)) return String(val);
      return n.toFixed(3);
    };

    const formatDate = (dateString) => {
      if (!dateString) return '';
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      // Date-only from API (YYYY-MM-DD) must be treated as a calendar day in local time — not UTC (avoids ±1 day in many zones).
      let date;
      if (typeof dateString === 'string') {
        const m = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (m) {
          date = new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
        }
      }
      if (!date || Number.isNaN(date.getTime())) {
        date = new Date(dateString);
      }
      if (Number.isNaN(date.getTime())) return '';
      return `${months[date.getMonth()]} ${String(date.getDate()).padStart(2, '0')}, ${date.getFullYear()}`;
    };

    const apiNumToStr = (v) => (v == null || v === '' ? '' : String(v));

    /** Position in API payload — used when date/other sort keys tie (API usually returns oldest first). */
    let apiIndex = 0;

    apiData.forEach((item) => {
      // Process details - create a record for each detail
      if (item.details && item.details.length > 0) {
        item.details.forEach((detail) => {
          const baseRecord = {
            id: recordId++,
            apiIndex: apiIndex++,
            stockId: item.stockId || item.stockInId || item.id, // Store API ID for delete/update operations
            rawDate: item.date || '',
            date: formatDate(item.date),
            materialtype: item.materialType || '',
            grade: detail.grade || '-',
            qty: detail.quantity ? detail.quantity.split(' ')[0] : '',
            qtyUnit: (() => {
              if (!detail.quantity) return '';
              const parts = detail.quantity.split(' ');
              if (parts.length > 1) {
                // Check if it contains sq.mm (format: "qty unit sqmm sq.mm")
                const sqmmIndex = parts.findIndex(part => part === 'sq.mm');
                if (sqmmIndex > 1) {
                  // Return only the unit part (e.g., "Meters" from "Meters 2.5 sq.mm")
                  return parts.slice(1, sqmmIndex - 1).join(' ');
                }
                // Return everything after qty
                return parts.slice(1).join(' ');
              }
              return '';
            })(),
            sqmm: (() => {
              if (!detail.quantity) return '';
              const parts = detail.quantity.split(' ');
              // Check if it contains sq.mm (format: "qty unit sqmm sq.mm")
              const sqmmIndex = parts.findIndex(part => part === 'sq.mm');
              if (sqmmIndex > 1) {
                // Return the sqmm value (the part before "sq.mm")
                return parts[sqmmIndex - 1] || '';
              }
              return '';
            })(),
            vendor: item.vendor || '',
            totalamount: apiNumToStr(item.totalAmount),
            ...(() => {
              const gst = splitGstFromApi(item);
              return {
                gstNumber: gst.gstNumber,
                gstPercentage: gst.gstPercentage,
                withGST: computeAmountWithGST(item.totalAmount, gst.gstPercentage),
              };
            })(),
            supervisor: item.supervisor || '',
            grossWeight: (detail.grossWeight ?? detail.gross_weight ?? item.grossWeight ?? item.gross_weight) != null ? String(detail.grossWeight ?? detail.gross_weight ?? item.grossWeight ?? item.gross_weight) : '',
            tareWeight: (detail.tareWeight ?? detail.tare_weight ?? item.tareWeight ?? item.tare_weight) != null ? String(detail.tareWeight ?? detail.tare_weight ?? item.tareWeight ?? item.tare_weight) : '',
            netWeight: (detail.netWeight ?? detail.net_weight ?? item.netWeight ?? item.net_weight) != null
              ? formatWeight3(detail.netWeight ?? detail.net_weight ?? item.netWeight ?? item.net_weight)
              : '',
            quantity: quantityDisplayNoWeightUnit(
              detail.quantity != null ? String(detail.quantity) : ''
            ),
            rate: (detail.rate ?? item.rate ?? detail.rate) != null ? String(detail.rate ?? item.rate) : '',
            selectedWeight: (detail.selectedWeight ?? detail.selected_weight ?? item.selectedWeight ?? item.selected_weight) != null ? String(detail.selectedWeight ?? detail.selected_weight ?? item.selectedWeight ?? item.selected_weight) : '',
            invoiceNumber: item.invoiceNumber || item.invoice_number || '',
            originalData: item, // Store original API data
          };

          // For plant records, add plant field and dc
          if (isPlant) {
            baseRecord.plant = item.plant || '';
            baseRecord.dc = item.dc || item.deliveryChallan || '-';
            baseRecord.invoiceNumber = item.invoiceNumber || item.invoice_number || '';
          } else {
            // For site records, add sitecode and dc (handle both dc and deliveryChallan from API)
            baseRecord.sitecode =
              item.siteCode || item.sitecode || item.site_code || '';
            baseRecord.dc = item.dc || item.deliveryChallan || '-';
            baseRecord.invoiceNumber = item.invoiceNumber || item.invoice_number || '';
          }

          records.push(baseRecord);
        });
      } else {
        // If no details, create a single record with default values
        const baseRecord = {
            id: recordId++,
            apiIndex: apiIndex++,
            stockId: item.stockId || item.stockInId || item.id, // Store API ID for delete/update operations
            rawDate: item.date || '',
            date: formatDate(item.date),
          materialtype: item.materialType || '',
          grade: '-',
          qty: '',
          qtyUnit: '',
          vendor: item.vendor || '',
          totalamount: apiNumToStr(item.totalAmount),
          ...(() => {
            const gst = splitGstFromApi(item);
            return {
              gstNumber: gst.gstNumber,
              gstPercentage: gst.gstPercentage,
              withGST: computeAmountWithGST(item.totalAmount, gst.gstPercentage),
            };
          })(),
          supervisor: item.supervisor || '',
          grossWeight: '',
          tareWeight: '',
          netWeight: '',
          quantity: '',
          rate: '',
          selectedWeight: (item.selectedWeight ?? item.selected_weight) != null ? String(item.selectedWeight ?? item.selected_weight) : '',
          originalData: item, // Store original API data
        };

        // For plant records, add plant field and dc
        if (isPlant) {
          baseRecord.plant = item.plant || '';
          baseRecord.dc = item.dc || item.deliveryChallan || '-';
          baseRecord.invoiceNumber = item.invoiceNumber || item.invoice_number || '';
        } else {
          // For site records, add sitecode and dc (handle both dc and deliveryChallan from API)
          baseRecord.sitecode =
            item.siteCode || item.sitecode || item.site_code || '';
          baseRecord.dc = item.dc || item.deliveryChallan || '-';
          baseRecord.invoiceNumber = item.invoiceNumber || item.invoice_number || '';
        }

        records.push(baseRecord);
      }
    });

    records.sort(compareRecordsNewestFirst);
    return records;
  };

  // Helper function to fetch data from API
  const fetchWithCorsFallback = async (url) => {
    try {
      const response = await fetch(url, {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching data:', error);
      throw error;
    }
  };

  // Fetch data from API
  const fetchStockInData = async () => {
    setIsLoading(true);
    try {
      // Fetch plant data
      const plantApiUrl = `${API_BASE_URL}/api/stockIn/bySiteType?siteType=plant`;
      const plantData = await fetchWithCorsFallback(plantApiUrl);
      const plantRecords = transformApiData(plantData, true);
      setStockInPlant(plantRecords);

      // Fetch site data
      const siteApiUrl = `${API_BASE_URL}/api/stockIn/bySiteType?siteType=site`;
      const siteData = await fetchWithCorsFallback(siteApiUrl);
      const siteRecords = transformApiData(siteData, false);
      setStockInSite(siteRecords);
    } catch (error) {
      console.error('Error fetching stock in data:', error);
      // Keep existing data if fetch fails
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchStockInData();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/maintence/Plant`, { method: 'GET', mode: 'cors' });
        if (!res.ok) return;
        const data = await res.json();
        const list = Array.isArray(data) ? data : [];
        const filtered = list.filter((r) => !r.type || String(r.type).toLowerCase() === 'plant');
        const opts = filtered
          .map((r) => ({
            value: String(r.value ?? r.code ?? '').trim(),
            label: String(r.value ?? r.code ?? '').trim(),
          }))
          .filter((o) => o.value);
        if (!cancelled && opts.length) setMaintenancePlantOptions(opts);
      } catch {
        /* keep seeded plant list */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/maintence/Site`, { method: 'GET', mode: 'cors' });
        if (!res.ok) return;
        const data = await res.json();
        const list = Array.isArray(data) ? data : [];
        const filtered = list.filter((r) => !r.type || String(r.type).toLowerCase() === 'site');
        const seen = new Set();
        const opts = filtered
          .map((r) => {
            const label = String(r.value ?? r.code ?? '').trim();
            if (!label) return null;
            const value = label.replace(/\s+/g, '').toLowerCase();
            if (seen.has(value)) return null;
            seen.add(value);
            return {
              id: r.id,
              value,
              label,
            };
          })
          .filter(Boolean)
          .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
        const siteOptsNoOthers = opts.filter((o) => String(o.value).toLowerCase() !== 'others');
        if (!cancelled) setMaintenanceSiteOptions(siteOptsNoOthers);
      } catch {
        /* keep seeded site list */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/maintence/Vendor`, { method: 'GET', mode: 'cors' });
        if (!res.ok) return;
        const data = await res.json();
        const list = Array.isArray(data) ? data : [];
        const filtered = list.filter((r) => !r.type || String(r.type).toLowerCase() === 'vendor');
        const seen = new Set();
        const opts = filtered
          .map((r) => {
            const label = String(r.value ?? r.code ?? '').trim();
            if (!label) return null;
            const key = label.toLowerCase();
            if (seen.has(key)) return null;
            seen.add(key);
            return { value: label, label };
          })
          .filter(Boolean)
          .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
        if (!cancelled) setMaintenanceVendorOptions(opts);
      } catch {
        /* keep existing fallback behavior */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!maintenancePlantOptions.length || isEditMode) return;
    setFormData((prev) => ({ ...prev, plant: maintenancePlantOptions[0].value }));
  }, [maintenancePlantOptions, isEditMode]);

  const [stockInPlant, setStockInPlant] = useState([
    {
      id: 1,
      date: "Oct 01, 2025",
      materialtype: "MSAND",
      grade: "Nice",
      qty: "140",
      qtyUnit: "unit",
      vendor: "MMR",
      totalamount: "10000",
      withGST: "10500.00",
      advance: "1500",
      balance: "1000",
      supervisor: "Selva",
    },
    {
      id: 2,
      date: "Oct 02, 2025",
      materialtype: "Aggregate",
      grade: "Chips",
      qty: "410",
      qtyUnit: "metric tons",
      vendor: "RRK",
      totalamount: "15000",
      withGST: "15750.00",
      advance: "6000",
      balance: "1000",
      supervisor: "Kumar",
    },
    {
      id: 3,
      date: "Oct 03, 2025",
      materialtype: "Cement",
      grade: "PPC",
      qty: "520",
      qtyUnit: "metric tons",
      vendor: "National",
      totalamount: "10000",
      withGST: "10500.00",
      advance: "3000",
      balance: "1000",
      supervisor: "Sekar",
    },
    {
      id: 4,
      date: "Oct 04, 2025",
      materialtype: "Dust",
      grade: "-",
      qty: "600",
      qtyUnit: "metric tons",
      vendor: "MMR",
      totalamount: "35000",
      withGST: "36750.00",
      advance: "",
      balance: "1000",
      supervisor: "Senthil",
    },
    {
      id: 5,
      date: "Oct 05, 2025",
      materialtype: "Bitamin(Thar)",
      grade: "-",
      qty: "820",
      qtyUnit: "metric tons",
      vendor: "JJ",
      totalamount: "20000",
      withGST: "21000.00",
      advance: "7000",
      balance: "1000",
      supervisor: "Jana",
    },
    {
      id: 6,
      date: "Oct 06, 2025",
      materialtype: "LDO Oil",
      grade: "-",
      qty: "582",
      qtyUnit: "ltr",
      vendor: "TRN",
      totalamount: "40000",
      withGST: "42000.00",
      advance: "",
      balance: "1000",
      supervisor: "Kathir",
    },
    {
      id: 7,
      date: "Oct 07, 2025",
      materialtype: "Diesel",
      grade: "-",
      qty: "985",
      qtyUnit: "ltr",
      vendor: "LLP",
      totalamount: "25000",
      withGST: "26250.00",
      advance: "-", 
      balance: "1000",
      supervisor: "Sathik",
    },
    {
      id: 8,
      date: "Oct 07, 2025",
      materialtype: " Aggregate",
      grade: "-",
      qty: "456",
      qtyUnit: "metric tons",
      vendor: "RRK",
      totalamount: "50000",
      withGST: "52500.00",
      advance: "-",
      balance: "1000",
      supervisor: "Saran",
    },
    {
      id: 9,
      date: "Oct 07, 2025",
      materialtype: "Cement",
      grade: "-",
      qty: "200",
      qtyUnit: "metric tons",
      vendor: "JJN",
      totalamount: "40000",
      withGST: "42000.00",
      advance: "",
      balance: "1000",
      supervisor: "Vignesh",
    },
    {
      id: 10,
      date: "Oct 07, 2025",
      materialtype: "Dust",
      grade: "-",
      qty: "874",
      qtyUnit: "metric tons",
      vendor: "Arjun",
      totalamount: "20000",
      withGST: "21000.00",
      advance: "8000",
      balance: "1000",
      supervisor: "Sudha",
    },
  ]);

  const [stockInSite, setStockInSite] = useState([
    {
      id: 1,
      date: "Oct 01, 2025",
      sitecode: "ST1998",
      materialtype: "Steel",
      grade: "6MM",
      qty: "140",
      vendor: "MMR",
      totalamount: "10000",
      withGST: "10500.00",
      advance: "1500",
      supervisor: "Selva",
      balance: "1000",
      dc: "DC123456", 
      },
    {
      id: 2,
      date: "Oct 02, 2025",
      sitecode: "ST2765",
      materialtype: "Aggregate",
      grade: "Chips",
      qty: "410",
      vendor: "RRK",
      totalamount: "15000",
      withGST: "15750.00",
      advance: "6000",
      supervisor: "Kumar",
      balance: "2000",
      dc: "DC123456", 
    },
    {
      id: 3,
      date: "Oct 03, 2025",
      sitecode: "ST3865",
      materialtype: "Cement",
      grade: "OPC",
      qty: "520(Metric tons)",
      vendor: "National",
      totalamount: "10000",
      withGST: "10500.00",
      advance: "3000",
      supervisor: "Sekar",
      balance: "2000",
      dc: "DC123456", 
    },
    {
      id: 4,
      date: "Oct 04, 2025",
      sitecode: "ST4765",
      materialtype: "PVC Pipes",
      grade: "12 ",
      qty: "600(Meter)",
      vendor: "MMR",
      totalamount: "35000",
      withGST: "36750.00",
      advance: "",
      supervisor: "Senthil",
      balance: "3000",
      dc: "DC123456", 
    },
    {
      id: 5,
      date: "Oct 05, 2025",
      sitecode: "ST19098",
      materialtype: "PVC Pipes",
      grade: "12",
      qty: "820(Meter)",
      vendor: "JJ",
      totalamount: "20000",
      withGST: "21000.00",
      advance: "7000",
      supervisor: "Jana",
      balance: "4000",
      dc: "DC123456", 
    },
    {
      id: 6,
      date: "Oct 06, 2025",
      sitecode: "ST2234",
      materialtype: "Wires",
      grade: "78",
      qty: "582(Coils)",
      vendor: "TRN",
      totalamount: "40000",
      withGST: "42000.00",
      advance: "",
      supervisor: "Kathir",
      balance: "4500",
      dc: "DC123456", 
    },
    {
      id: 7,
      date: "Oct 07, 2025",
      sitecode: "ST3765",
      materialtype: "FRP Manual Door",
      grade: "600",
      qty: "9MM",
      vendor: "LLP",
      totalamount: "25000",
      withGST: "26250.00",
      advance: "-",
      supervisor: "Sathik",
      balance: "7500",
      dc: "DC123456", 
    },
    {
      id: 8,
      date: "Oct 07, 2025",
      sitecode: "ST4345",
      materialtype: "FRP Catchpit",
      grade: "450*450",
      qty: "-",
      vendor: "RRK",
      totalamount: "50000",
      withGST: "52500.00",
      advance: "-",
      supervisor: "Saran",
      balance: "8000",
      dc: "DC123456", 
    },
    {
      id: 9,
      date: "Oct 07, 2025",
      sitecode: "ST29098",
      materialtype: "CI Catchpit",
      grade: "600*600",
      qty: "-",
      vendor: "JJN",
      totalamount: "40000",
      withGST: "42000.00",
      advance: "",
      supervisor: "Vignesh",
      balance: "-",
      dc: "DC123456", 
    },
    {
      id: 10,
      date: "Oct 07, 2025",
      sitecode: "ST1765",
      materialtype: "FRP RWH",
      grade: "600",
      qty: "-",
      vendor: "Arjun",
      totalamount: "20000",
      withGST: "21000.00",
      advance: "8000",
      supervisor: "Sudha",
      balance: "-",
      dc: "DC123456", 
    },
  ]);

  const plantMaterial = useMemo(() => {
    if (plantMaterialsFromApi.length > 0) {
      return plantMaterialsFromApi
        .map((m) => {
          const name = String(m.materialType ?? m.materialtype ?? '').trim();
          return name ? { value: name, label: name } : null;
        })
        .filter(Boolean);
    }
    // Fallback to hardcoded list when API returns nothing
    return [
      { value: "MSAND", label: "MSAND" },
      { value: "aggregate", label: "Aggregate" },
      { value: "cement", label: "Cement" },
      { value: "dust", label: "Dust" },
      { value: "bitman", label: "Bitumen Thar" },
      { value: "ldooil", label: "LDO Oil" },
      { value: "diesel", label: "Diesel" },
    ];
  }, [plantMaterialsFromApi]);

  const siteMaterial = useMemo(() => {
    if (siteMaterialsFromApi.length > 0) {
      return siteMaterialsFromApi
        .map((m) => {
          const name = String(m.materialType ?? m.materialtype ?? '').trim();
          return name ? { value: name, label: name } : null;
        })
        .filter(Boolean);
    }
    // Fallback to hardcoded list when API returns nothing
    return [
      { value: "steel", label: "Steel" },
      { value: "aggregate", label: "Aggregate" },
      { value: "cement", label: "Cement" },
      { value: "pvcpipes", label: "PVC Pipes" },
      { value: "wires", label: "Wires" },
      { value: "frpmanualdoor", label: "FRP Manual Door" },
      { value: "frpcatchpit", label: "FRP Catchpit" },
      { value: "ci", label: "CI Catchpit" },
      { value: "frprwh", label: "FRP RWH" },
    ];
  }, [siteMaterialsFromApi]);

  const currentData = selectedOption === 'plant' ? stockInPlant : stockInSite;
  const setCurrentData = selectedOption === 'plant' ? setStockInPlant : setStockInSite;

  // Build filterable option lists
  const productOptions = useMemo(() => {
    const opts = selectedOption === 'plant' ? plantMaterial : siteMaterial;
    return [{ value: 'all', label: 'All' }, ...opts.filter((o) => o.value !== 'others')];
  }, [selectedOption]);

  const storeOptions = useMemo(() => {
    if (selectedOption === 'site') {
      const codes = siteCodes
        .filter((o) => String(o.value).toLowerCase() !== 'others')
        .map((o) => ({ value: o.label, label: o.label }));
      return [{ value: 'all', label: 'All' }, ...codes];
    }
    // For plant view, we do not have explicit stores; keep just All
    return [{ value: 'all', label: 'All' }];
  }, [selectedOption, siteCodes]);

  const vendorOptions = useMemo(() => {
    return [{ value: 'all', label: 'All' }, ...maintenanceVendorOptions];
  }, [maintenanceVendorOptions]);

  const modalVendorOptions = useMemo(() => {
    return maintenanceVendorOptions.map((v) => v.value);
  }, [maintenanceVendorOptions]);

  const supervisorOptions = useMemo(() => {
    if (supervisorsFromApi && supervisorsFromApi.length > 0) {
      return [{ value: 'all', label: 'All' }, ...supervisorsFromApi];
    }
    const supervisors = [...new Set(currentData.map(item => item.supervisor).filter(Boolean))].sort();
    return [{ value: 'all', label: 'All' }, ...supervisors.map(s => ({ value: s, label: s }))];
  }, [currentData, supervisorsFromApi]);


  // Compute filtered data for report/table
  // Check if any filters are applied
  const hasActiveFilters = useMemo(() => {
    return datePreset !== '' || dateDisplay !== '' || selectedStore !== 'all' || selectedProduct !== 'all' || selectedVendor !== 'all' || selectedSupervisor !== 'all';
  }, [datePreset, dateDisplay, selectedStore, selectedProduct, selectedVendor, selectedSupervisor]);

  // Date helpers (must be before filteredData - same as userDetails.js)
  const isDateInRange = (date, startDate, endDate) => {
    if (!startDate || !endDate || !date) return false;
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const s = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
    const e = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
    return d >= s && d <= e;
  };
  const parseDateValue = (dateValue) => {
    if (!dateValue) return new Date(0);
    if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      const [year, month, day] = dateValue.split('-').map(Number);
      return new Date(year, month - 1, day);
    }
    if (typeof dateValue === 'string') {
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const parts = dateValue.split(' ');
      if (parts.length === 3) {
        const monthIdx = months.indexOf(parts[0]);
        const day = parseInt(parts[1].replace(',', ''), 10);
        const year = parseInt(parts[2], 10);
        if (monthIdx !== -1 && !isNaN(day) && !isNaN(year)) {
          return new Date(year, monthIdx, day);
        }
      }
    }
    const parsed = new Date(dateValue);
    return Number.isNaN(parsed.getTime())
      ? new Date(0)
      : new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
  };

  const filteredData = useMemo(() => {
    // Determine active date range based on preset or custom selection (same as userDetails.js)
    let startDate = null;
    let endDate = null;
    const today = new Date();

    if (datePreset && datePreset !== 'custom') {
      switch (datePreset) {
        case 'today': {
          const d = new Date(today.getFullYear(), today.getMonth(), today.getDate());
          startDate = d;
          endDate = d;
          break;
        }
        case 'yesterday': {
          const d = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
          startDate = d;
          endDate = d;
          break;
        }
        case 'last7': {
          endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
          startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 6);
          break;
        }
        case 'last30': {
          endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
          startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 29);
          break;
        }
        case 'thisMonth': {
          startDate = new Date(today.getFullYear(), today.getMonth(), 1);
          endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
          break;
        }
        case 'lastMonth': {
          startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
          endDate = new Date(today.getFullYear(), today.getMonth(), 0);
          break;
        }
        default:
          break;
      }
    } else if (datePreset === 'custom' && customStartDate && customEndDate) {
      startDate = customStartDate;
      endDate = customEndDate;
    }

    let filtered = currentData.filter(item => {
      const storeOk = selectedOption === 'site'
        ? (selectedStore === 'all' || (item.sitecode || '').toString().toLowerCase() === selectedStore.toString().toLowerCase())
        : true;
      const productOk = selectedProduct === 'all' || (item.materialtype || '').toString().toLowerCase() === selectedProduct.toString().toLowerCase();
      // Vendor filter now uses free-text search against vendor name
      const vendorSearchTerm = String(vendorInputValue ?? '').trim().toLowerCase();
      const vendorOk = !vendorSearchTerm || (item.vendor || '').toString().toLowerCase().includes(vendorSearchTerm);
      const supervisorOk = selectedSupervisor === 'all' || (item.supervisor || '').toString().trim().toLowerCase() === selectedSupervisor.toString().trim().toLowerCase();

      let dateOk = true;
      if (startDate && endDate) {
        const itemDate = parseDateValue(item.date);
        dateOk = isDateInRange(itemDate, startDate, endDate);
      }

      return storeOk && productOk && vendorOk && supervisorOk && dateOk;
    });

    // Apply sorting
    if (sortColumn) {
      filtered = [...filtered].sort((a, b) => {
        let aValue = a[sortColumn];
        let bValue = b[sortColumn];

        // Handle different data types
        if (sortColumn === 'date') {
          aValue = parseRecordDateTime(a.rawDate || aValue);
          bValue = parseRecordDateTime(b.rawDate || bValue);
        } else if (sortColumn === 'totalamount' || sortColumn === 'withGST' || sortColumn === 'qty') {
          // Handle numeric values
          aValue = parseFloat(aValue) || 0;
          bValue = parseFloat(bValue) || 0;
        } else {
          // Handle string values
          aValue = (aValue || '').toString().toLowerCase();
          bValue = (bValue || '').toString().toLowerCase();
        }

        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return compareRecordsNewestFirst(a, b);
      });
    }

    return filtered;
  }, [currentData, selectedOption, selectedStore, selectedProduct, vendorInputValue, selectedSupervisor, sortColumn, sortDirection, datePreset, customStartDate, customEndDate]);

  // Pagination logic (based on filtered results)
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, currentPage, itemsPerPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column; date defaults to newest-first
      setSortColumn(column);
      setSortDirection(defaultSortDirectionForColumn(column));
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  const getSortIcon = (column) => {
    if (sortColumn !== column) {
      return (
        <svg className="w-4 h-4 text-white opacity-70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    if (sortDirection === 'asc') {
      return (
        <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
        </svg>
      );
    }
    return (
      <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  const toggleColumn = (columnKey) => {
    setVisibleColumns(prev => {
      const visibleCount = Object.values(prev).filter(v => v).length;
      if (prev[columnKey] && visibleCount === 1) {
        return prev;
      }
      return {
        ...prev,
        [columnKey]: !prev[columnKey]
      };
    });
  };
  const handleResetColumns = () => {
    setVisibleColumns(defaultVisibleColumns);
  };

  const columnDefinitions = selectedOption === 'site' ? [
    { key: 'date', label: 'Date' },
    { key: 'sitecode', label: 'Site Code' },
    { key: 'invoiceNumber', label: 'Invoice No.' },
    { key: 'gstPercentage', label: 'GST(%)' },
    { key: 'dc', label: 'DC' },
    { key: 'materialtype', label: 'Material Type' },
    { key: 'grade', label: 'Grade' },
    { key: 'grossWeight', label: 'Gross Weight' },
    { key: 'tareWeight', label: 'Tare Weight' },
    { key: 'netWeight', label: 'Net Weight' },
    { key: 'quantity', label: 'Quantity' },
    { key: 'rate', label: 'Rate' },
    { key: 'weight', label: 'Weight' },
    { key: 'qty', label: 'Qty' },
    { key: 'totalamount', label: 'Total Amount' },
    { key: 'withGST', label: 'With GST' },
    { key: 'gstNumber', label: 'GST Number' },
    { key: 'vendor', label: 'Vendor' },
    { key: 'supervisor', label: 'Supervisor' },
    { key: 'actions', label: 'Actions' }
  ] : [
    { key: 'date', label: 'Date' },
    { key: 'plant', label: 'Plant' },
    { key: 'invoiceNumber', label: 'Invoice No.' },
    { key: 'gstPercentage', label: 'GST(%)' },
    { key: 'dc', label: 'DC' },
    { key: 'materialtype', label: 'Material Type' },
    { key: 'grade', label: 'Grade' },
    { key: 'grossWeight', label: 'Gross Weight' },
    { key: 'tareWeight', label: 'Tare Weight' },
    { key: 'netWeight', label: 'Net Weight' },
    { key: 'quantity', label: 'Quantity' },
    { key: 'rate', label: 'Rate' },
    { key: 'weight', label: 'Weight' },
    { key: 'qty', label: 'Qty' },
    { key: 'totalamount', label: 'Total Amount' },
    { key: 'withGST', label: 'With GST' },
    { key: 'gstNumber', label: 'GST Number' },
    { key: 'vendor', label: 'Vendor' },
    { key: 'supervisor', label: 'Supervisor' },
    { key: 'actions', label: 'Actions' }
  ];

  const getVisibleColumnCount = () => {
    return Object.values(visibleColumns).filter(v => v).length;
  };

  // Date preset display and handling (same format as userDetails: Mar 16, 2026)
  useEffect(() => {
    if (!datePreset) {
      setDateDisplay('');
      return;
    }
    
    const now = new Date();
    const toDisplay = (d) => d.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
    });
    const clone = (d) => new Date(d.getTime());
    let start = clone(now);
    let end = clone(now);
    
    switch (datePreset) {
      case 'today':
        break;
      case 'yesterday':
        start.setDate(start.getDate() - 1); 
        end.setDate(end.getDate() - 1); 
        break;
      case 'last7':
        start.setDate(start.getDate() - 6); 
        break;
      case 'last30':
        start.setDate(start.getDate() - 29); 
        break;
      case 'thisMonth':
        start = new Date(now.getFullYear(), now.getMonth(), 1); 
        break;
      case 'lastMonth': {
        const firstLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
        start = firstLastMonth; 
        end = lastLastMonth; 
        break;
      }
      case 'custom':
        if (!dateDisplay) {
          setDateDisplay(`${toDisplay(start)} - ${toDisplay(end)}`);
        }
        return;
      default:
        break;
    }
    setDateDisplay(`${toDisplay(start)} - ${toDisplay(end)}`);
  }, [datePreset]);

  const handleDatePresetChange = (preset) => {
    setDatePreset(preset);
    setShowDateDropdown(false);
    if (preset === 'custom') {
      setShowCustomCalendar(true);
    } else {
      setShowCustomCalendar(false);
    }
  };

  // Calendar helper functions
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const formatDate = (date) => {
    if (!date) return '';
    // Match "Mar 16, 2026" style (same as userDetails.js)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
    });
  };

  const isSameDate = (date1, date2) => {
    if (!date1 || !date2) return false;
    return date1.getFullYear() === date2.getFullYear() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getDate() === date2.getDate();
  };

  const handleDateClick = (date) => {
    if (!customStartDate || (customStartDate && customEndDate)) {
      setCustomStartDate(date);
      setCustomEndDate(null);
    } else if (customStartDate && !customEndDate) {
      if (date < customStartDate) {
        setCustomEndDate(customStartDate);
        setCustomStartDate(date);
      } else {
        setCustomEndDate(date);
      }
    }
  };

  const handleApplyCustomRange = () => {
    if (customStartDate && customEndDate) {
      setDateDisplay(`${formatDate(customStartDate)} - ${formatDate(customEndDate)}`);
      setShowCustomCalendar(false);
      setShowDateDropdown(false);
    }
  };

  const handleCancelCustomRange = () => {
    setShowCustomCalendar(false);
    setShowDateDropdown(false);
    setCustomStartDate(null);
    setCustomEndDate(null);
  };

  const handleClearDate = (e) => {
    e.stopPropagation();
    setDatePreset('');
    setDateDisplay('');
    setCustomStartDate(null);
    setCustomEndDate(null);
    setShowCustomCalendar(false);
    setShowDateDropdown(false);
  };

  const handleClearStore = (e) => {
    e.stopPropagation();
    setSelectedStore('all');
    setStoreInputValue('');
    setShowStoreDropdown(false);
    setCurrentPage(1);
  };

  const handleClearProduct = (e) => {
    e.stopPropagation();
    setSelectedProduct('all');
    setProductInputValue('');
    setShowProductDropdown(false);
    setCurrentPage(1);
  };

  const handleClearVendor = (e) => {
    e.stopPropagation();
    setSelectedVendor('all');
    setVendorInputValue('');
    setShowVendorDropdown(false);
    setCurrentPage(1);
  };

  const handleClearSupervisor = (e) => {
    e.stopPropagation();
    setSelectedSupervisor('all');
    setSupervisorInputValue('');
    setShowSupervisorDropdown(false);
    setCurrentPage(1);
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      newMonth.setMonth(prev.getMonth() + direction);
      return newMonth;
    });
  };

  const handleClearFilters = () => {
    setDatePreset('');
    setDateDisplay('');
    setCustomStartDate(null);
    setCustomEndDate(null);
    setSelectedStore('all');
    setSelectedProduct('all');
    setSelectedVendor('all');
    setSelectedSupervisor('all');
    setStoreInputValue('');
    setProductInputValue('');
    setVendorInputValue('');
    setSupervisorInputValue('');
    setCurrentPage(1);
    setShowCustomCalendar(false);
    setShowDateDropdown(false);
    setShowStoreDropdown(false);
    setShowProductDropdown(false);
    setShowVendorDropdown(false);
    setShowSupervisorDropdown(false);
  };

  // Store autocomplete logic
  const isTypingStoreRef = useRef(false);
  
  useEffect(() => {
    if (!isTypingStoreRef.current) {
      if (selectedStore === 'all') {
        setStoreInputValue('');
      } else {
        const selectedOption = storeOptions.find(opt => opt.value === selectedStore);
        if (selectedOption) {
          setStoreInputValue(selectedOption.label);
        } else {
          setStoreInputValue('');
        }
      }
    }
  }, [selectedStore, storeOptions]);

  const filteredStoreOptions = useMemo(() => {
    if (!storeInputValue.trim()) {
      return storeOptions;
    }
    const searchTerm = storeInputValue.toLowerCase();
    return storeOptions.filter(opt => 
      opt.label.toLowerCase().includes(searchTerm)
    );
  }, [storeOptions, storeInputValue]);

  const handleStoreInputChange = (e) => {
    const value = e.target.value;
    isTypingStoreRef.current = true;
    setStoreInputValue(value);
    setShowStoreDropdown(true);
    
    if (!value.trim()) {
      setSelectedStore('all');
    }
    
    setTimeout(() => {
      isTypingStoreRef.current = false;
    }, 100);
  };

  const handleStoreSelect = (option) => {
    isTypingStoreRef.current = false;
    setSelectedStore(option.value);
    setStoreInputValue(option.label);
    setShowStoreDropdown(false);
    setCurrentPage(1);
  };

  // Product autocomplete logic
  const isTypingProductRef = useRef(false);
  
  useEffect(() => {
    if (!isTypingProductRef.current) {
      if (selectedProduct === 'all') {
        setProductInputValue('');
      } else {
        const selectedOption = productOptions.find(opt => opt.value === selectedProduct);
        if (selectedOption) {
          setProductInputValue(selectedOption.label);
        } else {
          setProductInputValue('');
        }
      }
    }
  }, [selectedProduct, productOptions]);

  const filteredProductOptions = useMemo(() => {
    if (!productInputValue.trim()) {
      return productOptions;
    }
    const searchTerm = productInputValue.toLowerCase();
    return productOptions.filter(opt => 
      opt.label.toLowerCase().includes(searchTerm)
    );
  }, [productOptions, productInputValue]);

  const handleProductInputChange = (e) => {
    const value = e.target.value;
    isTypingProductRef.current = true;
    setProductInputValue(value);
    setShowProductDropdown(true);
    
    if (!value.trim()) {
      setSelectedProduct('all');
    }
    
    setTimeout(() => {
      isTypingProductRef.current = false;
    }, 100);
  };

  const handleProductSelect = (option) => {
    isTypingProductRef.current = false;
    setSelectedProduct(option.value);
    setProductInputValue(option.label);
    setShowProductDropdown(false);
    setCurrentPage(1);
  };

  // Vendor autocomplete logic
  const isTypingVendorRef = useRef(false);
  
  useEffect(() => {
    if (!isTypingVendorRef.current) {
      if (selectedVendor === 'all') {
        setVendorInputValue('');
      } else {
        const selectedOption = vendorOptions.find(opt => opt.value === selectedVendor);
        if (selectedOption) {
          setVendorInputValue(selectedOption.label);
        } else {
          setVendorInputValue('');
        }
      }
    }
  }, [selectedVendor, vendorOptions]);

  const filteredVendorOptions = useMemo(() => {
    if (!vendorInputValue.trim()) {
      return vendorOptions;
    }
    const searchTerm = vendorInputValue.toLowerCase();
    return vendorOptions.filter(opt => 
      opt.label.toLowerCase().includes(searchTerm)
    );
  }, [vendorOptions, vendorInputValue]);

  const handleVendorInputChange = (e) => {
    const value = e.target.value;
    isTypingVendorRef.current = true;
    setVendorInputValue(value);
    setShowVendorDropdown(true);
    
    if (!value.trim()) {
      setSelectedVendor('all');
    }
    
    setTimeout(() => {
      isTypingVendorRef.current = false;
    }, 100);
  };

  const handleVendorSelect = (option) => {
    isTypingVendorRef.current = false;
    setSelectedVendor(option.value);
    setVendorInputValue(option.label);
    setShowVendorDropdown(false);
    setCurrentPage(1);
  };

  // Supervisor autocomplete logic
  const isTypingSupervisorRef = useRef(false);
  
  useEffect(() => {
    if (!isTypingSupervisorRef.current) {
      if (selectedSupervisor === 'all') {
        setSupervisorInputValue('');
      } else {
        const selectedOption = supervisorOptions.find(opt => opt.value === selectedSupervisor);
        if (selectedOption) {
          setSupervisorInputValue(selectedOption.label);
        } else {
          setSupervisorInputValue('');
        }
      }
    }
  }, [selectedSupervisor, supervisorOptions]);

  const filteredSupervisorOptions = useMemo(() => {
    if (!supervisorInputValue.trim()) {
      return supervisorOptions;
    }
    const searchTerm = supervisorInputValue.toLowerCase();
    return supervisorOptions.filter(opt => 
      opt.label.toLowerCase().includes(searchTerm)
    );
  }, [supervisorOptions, supervisorInputValue]);

  const handleSupervisorInputChange = (e) => {
    const value = e.target.value;
    isTypingSupervisorRef.current = true;
    setSupervisorInputValue(value);
    setShowSupervisorDropdown(true);
    
    if (!value.trim()) {
      setSelectedSupervisor('all');
    }
    
    setTimeout(() => {
      isTypingSupervisorRef.current = false;
    }, 100);
  };

  const handleSupervisorSelect = (option) => {
    isTypingSupervisorRef.current = false;
    setSelectedSupervisor(option.value);
    setSupervisorInputValue(option.label);
    setShowSupervisorDropdown(false);
    setCurrentPage(1);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDateDropdown && !event.target.closest('.date-dropdown-container')) {
        setShowDateDropdown(false);
      }
      if (showStoreDropdown && !event.target.closest('.store-autocomplete-container')) {
        setShowStoreDropdown(false);
      }
      if (showProductDropdown && !event.target.closest('.product-autocomplete-container')) {
        setShowProductDropdown(false);
      }
      if (showVendorDropdown && !event.target.closest('.vendor-autocomplete-container')) {
        setShowVendorDropdown(false);
      }
      if (showSupervisorDropdown && !event.target.closest('.supervisor-autocomplete-container')) {
        setShowSupervisorDropdown(false);
      }
      if (showColumnMenu && !event.target.closest('.column-menu-container')) {
        setShowColumnMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDateDropdown, showStoreDropdown, showProductDropdown, showVendorDropdown, showSupervisorDropdown, showColumnMenu]);

  const handleExportPDF = () => {
    try {
      const doc = new jsPDF();
      
      // Add title
      doc.setFontSize(16);
      doc.text(`Stock In Report (${selectedOption === 'plant' ? 'Plant' : 'Site'})`, 14, 15);
      
      // Add date range if available
      if (dateDisplay) {
        doc.setFontSize(10);
        doc.text(`Date Range: ${dateDisplay}`, 14, 22);
      }
      
      // Check if there's data to export
      if (!filteredData || filteredData.length === 0) {
        doc.setFontSize(12);
        doc.text('No data available to export', 14, 30);
        doc.save(`StockIn_Report_${selectedOption}_${new Date().toISOString().slice(0,10)}.pdf`);
        return;
      }
      
      // Prepare table data based on selected option
      const tableData = filteredData.map(item => {
        if (selectedOption === 'site') {
          return [
            item.sitecode || '',
            item.date || '',
            item.invoiceNumber || '',
            item.gstNumber || '',
            item.materialtype || '',
            item.grade || '',
            formatQuantityColumn3(item.quantity || item.qty || ''),
            item.qtyUnit || '',
            item.vendor || '',
            item.totalamount || '',
            item.withGST || '',
            item.supervisor || '',
            item.dc || ''
          ];
        } else {
          return [
            item.date || '',
            item.invoiceNumber || '',
            item.gstNumber || '',
            item.dc || '',
            item.materialtype || '',
            item.grade || '',
            formatQuantityColumn3(item.quantity || item.qty || ''),
            item.qtyUnit || '',
            item.vendor || '',
            item.totalamount || '',
            item.withGST || '',
            item.supervisor || ''
          ];
        }
      });
      
      // Add table
      const headers = selectedOption === 'site'
        ? [['Site Code', 'Date', 'Invoice No.', 'GST No.', 'Material Type', 'Grade', 'Quantity', 'Quantity Unit', 'Vendor', 'Total Amount', 'With GST(5%)', 'Supervisor', 'DC']]
        : [['Date', 'Invoice No.', 'GST No.', 'DC', 'Material Type', 'Grade', 'Quantity', 'Quantity Unit', 'Vendor', 'Total Amount', 'With GST(5%)', 'Supervisor']];
      
      autoTable(doc, {
        startY: dateDisplay ? 28 : 22,
        head: headers,
        body: tableData,
        styles: { fontSize: 7 },
        headStyles: { fillColor: [0, 150, 136], textColor: 255 },
        margin: { top: 20 }
      });
      
      // Save the PDF
      doc.save(`StockIn_Report_${selectedOption}_${new Date().toISOString().slice(0,10)}.pdf`);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  const handleExportExcel = () => {
    const headers = selectedOption === 'site'
      ? ['Site Code','Date','Invoice No.','GST No.','Material Type','Grade','Quantity','Quantity Unit','Vendor','Total Amount','With GST(5%)','Supervisor','DC (Delivery Challan)']
      : ['Date','Invoice No.','GST No.','DC (Delivery Challan)','Material Type','Grade','Quantity','Quantity Unit','Vendor','Total Amount','With GST(5%)','Supervisor'];
    
    const rows = filteredData.map(item => (
      selectedOption === 'site'
        ? [item.sitecode, item.date, item.invoiceNumber || '', item.gstNumber || '', item.materialtype, item.grade, formatQuantityColumn3(item.quantity || item.qty || ''), item.qtyUnit || '', item.vendor, item.totalamount, item.withGST || '', item.supervisor, item.dc || '']
        : [item.date, item.invoiceNumber || '', item.gstNumber || '', item.dc || '', item.materialtype, item.grade, formatQuantityColumn3(item.quantity || item.qty || ''), item.qtyUnit || '', item.vendor, item.totalamount, item.withGST || '', item.supervisor]
    ));
    
    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Stock In Report');
    
    // Set column widths
    const columnWidths = selectedOption === 'site'
      ? [{ wch: 12 }, { wch: 12 }, { wch: 14 }, { wch: 16 }, { wch: 15 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 18 }]
      : [{ wch: 12 }, { wch: 14 }, { wch: 16 }, { wch: 15 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }];
    worksheet['!cols'] = columnWidths;
    
    XLSX.writeFile(workbook, `StockIn_Report_${selectedOption}_${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  const handleGenerateReport = () => {
    // Export filtered rows to CSV
    const headers = selectedOption === 'site'
      ? ['Site Code','Material Type','Grade','Quantity','Quantity Unit','Vendor','Total Amount','With GST(5%)','Supervisor','DC (Delivery Challan)']
      : ['Material Type','Grade','Quantity','Quantity Unit','Vendor','Total Amount','With GST(5%)','Supervisor','DC (Delivery Challan)'];
    const rows = filteredData.map(item => (
      selectedOption === 'site'
        ? [item.sitecode, item.materialtype, item.grade, formatQuantityColumn3(item.quantity || item.qty || ''), item.qtyUnit || '', item.vendor, item.totalamount, item.withGST || '', item.supervisor, item.dc || '']
        : [item.materialtype, item.grade, formatQuantityColumn3(item.quantity || item.qty || ''), item.qtyUnit || '', item.vendor, item.totalamount, item.withGST || '', item.supervisor, item.dc || '']
    ));
    const csv = [headers, ...rows].map(r => r.map(v => (v == null ? '' : String(v).replace(/"/g,'""'))).map(v => `"${v}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `StockIn_Report_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  /** With GST = total × (1 + GST%/100) using gstPercentage. */
  const withGSTFromTotal = (totalAmountStr, gstPercentageStr) =>
    computeAmountWithGST(totalAmountStr, gstPercentageStr);

  /** For materialUnit: "30" → "30 CFT"; "30 CFT" unchanged (avoids "30 CFT CFT"). */
  const normalizeCftQuantityForApi = (raw) => {
    if (raw == null) return '';
    const s = String(raw).trim();
    if (!s) return '';
    if (/\bcft\b/i.test(s)) return s;
    return `${s} CFT`;
  };

  /** For materialUnit when Cement + Bag (same pattern as CFT). */
  const normalizeBagQuantityForApi = (raw) => {
    if (raw == null) return '';
    const s = String(raw).trim();
    if (!s) return '';
    if (/\bbag\b/i.test(s)) return s;
    return `${s} Bag`;
  };

  /** Plant: Rate / Quantity / CFT total row — CFT or Bag. Works for both hardcoded and API materials. */
  const isPlantCftStyleWeight = (fd) => {
    const apiUnits = getApiUnits(fd.materialtype, 'plant');
    const apiHasCft = apiUnits.some((u) => matchMat(u, 'CFT'));
    const apiHasBag = apiUnits.some((u) => matchMat(u, 'Bag'));
    return (
      (fd.tonnageCFT === 'CFT' && (matchMat(fd.materialtype, 'MSAND', 'aggregate', 'cement', 'dust', 'bitman') || apiHasCft)) ||
      (fd.tonnageCFT === 'Bag' && (matchMat(fd.materialtype, 'cement') || apiHasBag))
    );
  };

  /** Site: CFT row for aggregate|cement, or Bag for Cement only. Works for both hardcoded and API materials. */
  const isSiteCftStyleWeight = (fd) => {
    const apiUnits = getApiUnits(fd.materialtype, 'site');
    const apiHasCft = apiUnits.some((u) => matchMat(u, 'CFT'));
    const apiHasBag = apiUnits.some((u) => matchMat(u, 'Bag'));
    return (
      (fd.tonnageCFT === 'CFT' && (matchMat(fd.materialtype, 'aggregate', 'cement') || apiHasCft)) ||
      (fd.tonnageCFT === 'Bag' && (matchMat(fd.materialtype, 'cement') || apiHasBag))
    );
  };

  const effectiveTotalForFinancials = (fd) => {
    if (modalSelectedOption === 'plant' && isPlantCftStyleWeight(fd)) {
      const cft = String(fd.cftTotalAmount ?? '').trim();
      if (cft) return cft;
    }
    if (modalSelectedOption === 'site' && isSiteCftStyleWeight(fd)) {
      const cft = String(fd.cftTotalAmount ?? '').trim();
      if (cft) return cft;
    }
    return fd.totalamount;
  };

  const applyWithGst = (state) => {
    const totalamount = effectiveTotalForFinancials(state);
    return {
      ...state,
      withGST: withGSTFromTotal(totalamount, state.gstPercentage),
    };
  };

  /** For request body `quantity` only — strips units (e.g. "30 CFT" → "30"). */
  const quantityNumericOnlyForApi = (raw) => {
    if (raw == null || raw === '') return '';
    const n = parseFloat(String(raw).trim());
    return Number.isFinite(n) ? String(n) : '';
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // If material type changes, clear grade field if it's no longer relevant
    if (name === 'materialtype') {
      const apiMat = getApiMaterialData(value, modalSelectedOption);
      const apiGrades = extractApiGrades(apiMat);
      const apiUnits = extractApiUnits(apiMat);
      const apiHasTonnageCFT = apiUnits.some((u) => matchMat(u, 'Tonnage', 'CFT'));
      const apiHasQtyUnit = apiUnits.length > 0 && !apiHasTonnageCFT;
      const shouldShowGrade = apiGrades.length > 0 ||
        (modalSelectedOption === 'plant' && matchMat(value, 'MSAND', 'aggregate', 'cement')) ||
        (modalSelectedOption === 'site' && matchMat(value, 'steel', 'aggregate', 'cement', 'pvcpipes', 'wires', 'frpmanualdoor', 'frpcatchpit', 'ci', 'frprwh', 'rmc', 'bt'));
      const shouldShowQtyUnit = apiHasQtyUnit ||
        (modalSelectedOption === 'site' && matchMat(value, 'pvcpipes', 'wires'));
      const shouldShowTonnageCFT = apiHasTonnageCFT ||
        (modalSelectedOption === 'plant' && matchMat(value, 'MSAND', 'aggregate', 'cement', 'dust', 'bitman')) ||
        (modalSelectedOption === 'site' && matchMat(value, 'aggregate', 'cement'));
      const shouldShowTonnageOnly = (modalSelectedOption === 'site' && matchMat(value, 'steel'));
      const shouldShowSqmm = modalSelectedOption === 'site' && matchMat(value, 'wires');
      setFormData(prev => ({
        ...prev,
        [name]: value,
        grade: shouldShowGrade ? prev.grade : '', // Clear grade if not relevant
        qtyUnit: shouldShowQtyUnit ? prev.qtyUnit : '', // Clear qtyUnit if not relevant
        sqmm: shouldShowSqmm ? prev.sqmm : '', // Clear sqmm if not relevant
        customMaterialType: value === 'others' ? prev.customMaterialType : '', // Clear custom material type if not "others"
        tonnageCFT: (shouldShowTonnageCFT || shouldShowTonnageOnly)
          ? shouldShowTonnageOnly
            ? 'Tonnage'
            : prev.tonnageCFT === 'Bag' && !matchMat(value, 'cement') && !apiUnits.some((u) => matchMat(u, 'Bag'))
              ? ''
              : prev.tonnageCFT
          : '' // Bag only valid for Cement; Steel → Tonnage
      }));
    }
    // If totalamount changes, auto-calculate withGST when GST(%) is present
    else if (name === 'totalamount') {
      setFormData(prev => applyWithGst({ ...prev, totalamount: value }));
    }
    // If GST(%) changes, recalculate withGST from current total
    else if (name === 'gstPercentage') {
      setFormData(prev => applyWithGst({ ...prev, gstPercentage: value }));
    }
    else if (name === 'gstNumber') {
      setFormData(prev => ({
        ...prev,
        gstNumber: formatIndianGstNumber(value)
      }));  
    }
    // If grossWeight or tareWeight changes, calculate netWeight
    else if (name === 'grossWeight' || name === 'tareWeight') {
      setFormData(prev => {
        const gross = name === 'grossWeight' ? parseFloat(value) || 0 : parseFloat(prev.grossWeight) || 0;
        const tare = name === 'tareWeight' ? parseFloat(value) || 0 : parseFloat(prev.tareWeight) || 0;
        const net = gross - tare;
        return {
          ...prev,
          [name]: value,
          // Ensure tonnage net weight shows exactly 3 decimal places
          netWeight: net > 0 ? net.toFixed(3) : ''
        };
      });
    }
    // If tonnageCFT changes, clear weight fields if empty
    else if (name === 'tonnageCFT') {
      setFormData(prev => ({
        ...prev,
        [name]: value,
        grossWeight: value === 'Tonnage' ? prev.grossWeight : '',
        tareWeight: value === 'Tonnage' ? prev.tareWeight : '',
        netWeight: value === 'Tonnage' ? prev.netWeight : '',
        rate: value === 'CFT' || value === 'Bag' ? prev.rate : '',
        quantity: value === 'CFT' || value === 'Bag' ? prev.quantity : '',
        cftTotalAmount: value === 'CFT' || value === 'Bag' ? prev.cftTotalAmount : ''
      }));
    }
    // If rate or quantity changes, calculate cftTotalAmount (for Plant MSAND, Aggregate, Cement, Dust, and Bitumen Thar CFT)
    else if (modalSelectedOption === 'plant' && (name === 'rate' || name === 'quantity') && 
             isPlantCftStyleWeight(formData)) {
      setFormData(prev => {
        const rate = name === 'rate' ? parseFloat(value) || 0 : parseFloat(prev.rate) || 0;
        const quantity = name === 'quantity' ? parseFloat(value) || 0 : parseFloat(prev.quantity) || 0;
        const total = rate * quantity;
        return applyWithGst({
          ...prev,
          [name]: value,
          cftTotalAmount: total > 0 ? total.toString() : ''
        });
      });
    }
    // If rate or quantity changes, calculate cftTotalAmount (for Site Aggregate, and Cement CFT)
    else if (modalSelectedOption === 'site' && (name === 'rate' || name === 'quantity') && 
             isSiteCftStyleWeight(formData)) {
      setFormData(prev => {
        const rate = name === 'rate' ? parseFloat(value) || 0 : parseFloat(prev.rate) || 0;
        const quantity = name === 'quantity' ? parseFloat(value) || 0 : parseFloat(prev.quantity) || 0;
        const total = rate * quantity;
        const totalStr = total > 0 ? total.toString() : '';
        return applyWithGst({
          ...prev,
          [name]: value,
          cftTotalAmount: totalStr,
          totalamount: totalStr,
        });
      });
    }
    // If rate or qty changes for Plant LDO Oil / Diesel, calculate totalamount as rate * qty
    else if (
      modalSelectedOption === 'plant' &&
      (name === 'rate' || name === 'qty') &&
      matchMat(formData.materialtype, 'ldooil', 'ldo oil', 'diesel')
    ) {
      setFormData(prev => {
        const rate = name === 'rate' ? parseFloat(value) || 0 : parseFloat(prev.rate) || 0;
        const quantity = name === 'qty' ? parseFloat(value) || 0 : parseFloat(prev.qty) || 0;
        const total = rate * quantity;
        const totalStr = total > 0 ? total.toString() : '';
        return applyWithGst({
          ...prev,
          [name]: value,
          totalamount: totalStr,
        });
      });
    }
    // If rate or qty changes for Site mode, calculate totalamount (exclude Steel, Aggregate, Cement when tonnageCFT is selected)
    else if (modalSelectedOption === 'site' && (name === 'rate' || name === 'qty') && formData.materialtype &&
             !((matchMat(formData.materialtype, 'steel') && formData.tonnageCFT === 'Tonnage') || (matchMat(formData.materialtype, 'aggregate', 'cement') && formData.tonnageCFT))) {
      setFormData(prev => {
        const rate = name === 'rate' ? parseFloat(value) || 0 : parseFloat(prev.rate) || 0;
        const quantity = name === 'qty' ? parseFloat(value) || 0 : parseFloat(prev.qty) || 0;
        const total = rate * quantity;
        const totalStr = total > 0 ? total.toString() : '';
        return applyWithGst({
          ...prev,
          [name]: value,
          totalamount: totalStr,
        });
      });
    }
    else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleNumberInput = (e) => {
    // Allow: backspace, delete, tab, escape, enter, decimal point
    if ([46, 8, 9, 27, 13, 110, 190].indexOf(e.keyCode) !== -1 ||
      // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      (e.keyCode === 65 && e.ctrlKey === true) ||
      (e.keyCode === 67 && e.ctrlKey === true) ||
      (e.keyCode === 86 && e.ctrlKey === true) ||
      (e.keyCode === 88 && e.ctrlKey === true) ||
      // Allow: home, end, left, right
      (e.keyCode >= 35 && e.keyCode <= 39)) {
      return;
    }
    // Ensure that it is a number and stop the keypress
    if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) {
      e.preventDefault();
    }
  };

  // Helper function to format date from YYYY-MM-DD to "Oct 01, 2025" format
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString + 'T00:00:00'); // Add time to avoid timezone issues
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[date.getMonth()];
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month} ${day}, ${year}`;
  };

  // Helper function to convert "Oct 01, 2025" or YYYY-MM-DD to YYYY-MM-DD for <input type="date"> (no UTC conversion).
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const s = String(dateString).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const parts = s.split(' ');
    if (parts.length === 3) {
      const monthIdx = months.indexOf(parts[0]);
      const day = parseInt(parts[1].replace(',', ''), 10);
      const year = parseInt(parts[2], 10);
      if (monthIdx !== -1 && !Number.isNaN(day) && !Number.isNaN(year)) {
        return `${year}-${String(monthIdx + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      }
    }
    return '';
  };

  const handleModalOptionChange = (e) => {
    const value = e.target.value;
    setModalSelectedOption(value);
    // Reset material type when switching between Plant and Site
    setFormData(prev => ({
      ...prev,
      materialtype: '',
      sitecode: value === 'plant' ? '' : prev.sitecode,
      rate: value === 'plant' ? '' : prev.rate // Clear rate when switching to Plant
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields based on current selection
    if (!formData.date) {
      alert('Please select a Date');
      return;
    }
    
    if (!formData.materialtype) {
      alert('Please select a Material Type');
      return;
    }
    
    // Validate quantity - skip if tonnageCFT is selected (for Plant or Site mode)
    const _plantApiHasTonnageCFT = getApiUnits(formData.materialtype, 'plant').some((u) => matchMat(u, 'Tonnage', 'CFT'));
    const _siteApiHasTonnageCFT = getApiUnits(formData.materialtype, 'site').some((u) => matchMat(u, 'Tonnage', 'CFT'));
    const hasTonnageCFT =
      (modalSelectedOption === 'plant' && (matchMat(formData.materialtype, 'MSAND', 'aggregate', 'cement', 'dust', 'bitman') || _plantApiHasTonnageCFT) && formData.tonnageCFT) ||
      (modalSelectedOption === 'site' && ((matchMat(formData.materialtype, 'steel') && formData.tonnageCFT === 'Tonnage') || (matchMat(formData.materialtype, 'aggregate', 'cement') || _siteApiHasTonnageCFT) && formData.tonnageCFT));
    
    if (!hasTonnageCFT) {
      const qtyStr = formData.qty != null ? String(formData.qty).trim() : '';
      const rateStr = formData.rate != null ? String(formData.rate).trim() : '';
      if (!qtyStr) {
        alert('Please enter Quantity');
        return;
      }
      if (!rateStr) {
        alert('Please enter Rate');
        return;
      }
    }
    
    // Validate plant for plant mode
    if (modalSelectedOption === 'plant' && !formData.plant) {
      alert('Please enter Plant name');
      return;
    }
    
    // Validate tonnageCFT for Steel, Aggregate, and Cement in Site mode
    if (modalSelectedOption === 'site' && matchMat(formData.materialtype, 'steel') && formData.tonnageCFT !== 'Tonnage') {
      alert('Please select Weight option (Tonnage)');
      return;
    }
    if (modalSelectedOption === 'site' && matchMat(formData.materialtype, 'aggregate', 'cement') && !formData.tonnageCFT) {
      alert('Please select Weight option (Tonnage, CFT, or Bag for Cement)');
      return;
    }
    
    // Validate weight fields when Tonnage is selected (Plant or Site mode)
    if (formData.tonnageCFT === 'Tonnage') {
      if (!formData.grossWeight) {
        alert('Please enter Gross Weight');
        return;
      }
      if (!formData.tareWeight) {
        alert('Please enter Tare Weight');
        return;
      }
    }
    
    // Validate CFT fields when CFT is selected (Plant or Site mode)
    if (isPlantCftStyleWeight(formData) || isSiteCftStyleWeight(formData)) {
      const rateStr = formData.rate != null ? String(formData.rate).trim() : '';
      const quantityStr = formData.quantity != null ? String(formData.quantity).trim() : '';
      if (!rateStr) {
        alert('Please enter Rate');
        return;
      }
      if (!quantityStr) {
        alert('Please enter Quantity');
        return;
      }
    }
    
    // Validate quantity unit for PVC Pipes and Wires in Site mode
    if (modalSelectedOption === 'site' && matchMat(formData.materialtype, 'pvcpipes', 'wires') && !formData.qtyUnit) {
      alert('Please select a Quantity Unit');
      return;
    }
    
    // Validate site code for site mode
    if (modalSelectedOption === 'site') {
      if (!formData.sitecode) {
        alert('Please select a Site Code');
        return;
      }
    }
    
    // Validate custom material type when "others" is selected
    if (formData.materialtype === 'others' && !formData.customMaterialType.trim()) {
      alert('Please enter a custom Material Type');
      return;
    }

    const gstNoRaw = String(formData.gstNumber ?? '').trim();
    if (gstNoRaw && !isValidIndianGstNumber(gstNoRaw)) {
      alert('Please enter a valid GST Number (15 characters, e.g. 33AAACH7409R1Z8)');
      return;
    }

    const gstPctRaw = String(formData.gstPercentage ?? '').trim();
    let gstPercentagePayload = null;
    if (gstPctRaw !== '') {
      const n = parseFloat(gstPctRaw);
      gstPercentagePayload = Number.isFinite(n) ? n : null;
    }
    
    // For Plant mode, call API to create stock in
    if (modalSelectedOption === 'plant' && !isEditMode) {
      try {
        // Convert dropdown values to display labels
        const materialOptions = plantMaterial;
        const materialLabel = formData.materialtype === 'others' 
          ? formData.customMaterialType 
          : (materialOptions.find(opt => opt.value === formData.materialtype)?.label || formData.materialtype);
        
        // Format date to YYYY-MM-DD for API
        const apiDate = formData.date; // Already in YYYY-MM-DD format from date input
        
        // Prepare materialGrade array - use grade if provided, otherwise use '-'
        const materialGrade = formData.grade ? [formData.grade] : ['-'];
        
        // Prepare materialUnit array - for Plant CFT / Cement Bag use quantity; else combine qty and qtyUnit
        const isPlantCftOrBag = isPlantCftStyleWeight(formData);
        let materialUnit;
        if (isPlantCftOrBag) {
          materialUnit = formData.quantity
            ? [
                formData.tonnageCFT === 'Bag'
                  ? normalizeBagQuantityForApi(formData.quantity)
                  : normalizeCftQuantityForApi(formData.quantity)
              ]
            : [''];
        } else if (matchMat(formData.materialtype, 'wires') && formData.qtyUnit && formData.sqmm) {
          materialUnit = [`${formData.qty} ${formData.qtyUnit} ${formData.sqmm} sq.mm`];
        } else if (formData.qtyUnit) {
          materialUnit = [`${formData.qty} ${formData.qtyUnit}`];
        } else {
          materialUnit = [formData.qty];
        }
        
        const totalAmount = isPlantCftOrBag
          ? (parseFloat(formData.cftTotalAmount) || 0)
          : (parseFloat(formData.totalamount) || 0);
        
        // For CFT, quantity is quantity; otherwise formData.quantity
        // const quantityValue = isPlantCFT ? (formData.quantity ?? '') : (formData.quantity ?? '');
        
        // Prepare request body matching API format
        const requestBody = {
          plant: formData.plant,
          gstNumber: formData.gstNumber || '',
          gstPercentage: gstPercentagePayload,
          invoiceNumber: formData.invoiceNumber || '',
          materialType: materialLabel,
          materialGrade: materialGrade,
          materialUnit: materialUnit,
          vendor: formData.vendor || '',
          totalAmount: totalAmount,
          supervisor: formData.supervisor || '',
          date: apiDate,
          stockType: 'plant',
          deliveryChallan: formData.dc || '',
          siteCode: formData.siteCode || 'string',
          grossWeight: formData.grossWeight ?? '',
          tareWeight: formData.tareWeight ?? '',
          netWeight: formData.netWeight ?? '',
          quantity: isPlantCftOrBag ? quantityNumericOnlyForApi(formData.quantity ?? '') : (formData.quantity ?? ''),
          rate: formData.rate ?? '',
          selectedWeight: formData.tonnageCFT ?? ''
        };
        
        // API call
        const apiUrl = `${API_BASE_URL}/api/stockIn/create/plant`;
        
        let response;
        try {
          response = await fetch(apiUrl, {
            method: 'POST',
            mode: 'cors',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody),
          });
        } catch (corsError) {
          // If CORS fails, we'll need to use a backend proxy or handle differently
          console.error('CORS Error:', corsError);
          alert('CORS Error: Unable to connect to the API server. Please configure CORS on the backend or use a backend proxy.');
          return;
        }
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
          throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
        }
        
        const responseData = await response.json();
        console.log('Stock In created successfully:', responseData);
        
        // Refresh plant data from API
        const plantApiUrl = `${API_BASE_URL}/api/stockIn/bySiteType?siteType=plant`;
        const plantData = await fetchWithCorsFallback(plantApiUrl);
        const plantRecords = transformApiData(plantData, true);
        setStockInPlant(plantRecords);
        
        // Switch to plant view to show the newly added data
        setSelectedOption('plant');
        setCurrentPage(1); // Reset to first page
        
        setSuccessMessage('Stock In added successfully!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        handleClose();
        return;
      } catch (error) {
        console.error('Error creating stock in:', error);
        alert(`Failed to create stock in: ${error.message}`);
        return;
      }
    }
    
    // For Site mode, call API to create stock in
    if (modalSelectedOption === 'site' && !isEditMode) {
      try {
        // Convert dropdown values to display labels
        const materialOptions = siteMaterial;
        const materialLabel = formData.materialtype === 'others' 
          ? formData.customMaterialType 
          : (materialOptions.find(opt => opt.value === formData.materialtype)?.label || formData.materialtype);
        
        const siteCodeValue =
          siteCodes.find((opt) => opt.value === formData.sitecode)?.label || formData.sitecode;
        
        // Format date to YYYY-MM-DD for API
        const apiDate = formData.date; // Already in YYYY-MM-DD format from date input
        
        // Prepare materialGrade array - use grade if provided, otherwise use '-'
        const materialGrade = formData.grade ? [formData.grade] : ['-'];
        
        // Prepare materialUnit array - handle tonnageCFT for Steel, Aggregate, and Cement
        let materialUnit;
        if (matchMat(formData.materialtype, 'steel') && formData.tonnageCFT === 'Tonnage') {
          // Use netWeight for Steel Tonnage
          materialUnit = formData.netWeight ? [`${formData.netWeight} metric tons`] : [''];
        } else if ((matchMat(formData.materialtype, 'aggregate', 'cement') || _siteApiHasTonnageCFT) && formData.tonnageCFT) {
          if (formData.tonnageCFT === 'Tonnage') {
            // Use netWeight for Tonnage
            materialUnit = formData.netWeight ? [`${formData.netWeight} metric tons`] : [''];
          } else if (formData.tonnageCFT === 'CFT') {
            // Use quantity for CFT
            materialUnit = formData.quantity ? [normalizeCftQuantityForApi(formData.quantity)] : [''];
          } else if (formData.tonnageCFT === 'Bag' && matchMat(formData.materialtype, 'cement')) {
            materialUnit = formData.quantity ? [normalizeBagQuantityForApi(formData.quantity)] : [''];
          } else {
            materialUnit = [formData.qty || ''];
          }
        } else if (matchMat(formData.materialtype, 'wires') && formData.qtyUnit) {
          // For Wires, include sqmm if provided
          if (formData.sqmm) {
            materialUnit = [`${formData.qty} ${formData.qtyUnit} ${formData.sqmm} sq.mm`];
          } else {
            materialUnit = [`${formData.qty} ${formData.qtyUnit}`];
          }
        } else {
          // Regular case - combine qty and qtyUnit if qtyUnit exists
          materialUnit = formData.qtyUnit 
            ? [`${formData.qty} ${formData.qtyUnit}`]
            : [formData.qty || ''];
        }
        
        // For tonnageCFT CFT mode, use cftTotalAmount; otherwise use totalamount
        const isSiteCftOrBag = isSiteCftStyleWeight(formData);
        const totalAmount = isSiteCftOrBag ? (parseFloat(formData.cftTotalAmount) || 0) : (parseFloat(formData.totalamount) || 0);
        
        // For CFT, quantity is normalized "30 CFT"; otherwise formData.quantity or formData.qty
        const quantityValue = isSiteCftOrBag
          ? quantityNumericOnlyForApi(formData.quantity ?? '')
          : (formData.quantity ?? formData.qty ?? '');
        
        // Prepare request body matching API format - ensure deliveryChallan is always included
        const requestBody = {
          siteCode: siteCodeValue || '',
          gstNumber: formData.gstNumber || '',
          gstPercentage: gstPercentagePayload,
          invoiceNumber: formData.invoiceNumber || '',
          materialType: materialLabel,
          materialGrade: materialGrade,
          materialUnit: materialUnit,
          vendor: formData.vendor || '',
          totalAmount: totalAmount,
          quantity: quantityValue,
          supervisor: formData.supervisor || '',
          date: apiDate,
          stockType: 'site',
          deliveryChallan: formData.dc || '',
          grossWeight: formData.grossWeight ?? '',
          tareWeight: formData.tareWeight ?? '',
          netWeight: formData.netWeight ?? '',
          rate: formData.rate ?? '',
          selectedWeight: formData.tonnageCFT ?? '',
          selectUnitType: formData.tonnageCFT ?? formData.qtyUnit ?? '',
          sqmm: formData.sqmm ?? ''
        };
        
        // Debug: Log the request body to verify deliveryChallan is included
        console.log('Site Create Request Body:', JSON.stringify(requestBody, null, 2));
        console.log('DC field value:', formData.dc);
        
        // Use CORS proxy for the API call
        const apiUrl = `${API_BASE_URL}/api/stockIn/create/site`;
        
        // For POST requests, try direct fetch first
        let response;
        try {
          response = await fetch(apiUrl, {
            method: 'POST',
            mode: 'cors',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody),
          });
        } catch (corsError) {
          // If CORS fails, we'll need to use a backend proxy or handle differently
          console.error('CORS Error:', corsError);
          alert('CORS Error: Unable to connect to the API server. Please configure CORS on the backend or use a backend proxy.');
          return;
        }
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
          throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
        }
        
        const responseData = await response.json();
        console.log('Stock In created successfully:', responseData);
        
        // Refresh site data from API
        const siteApiUrl = `${API_BASE_URL}/api/stockIn/bySiteType?siteType=site`;
        const siteData = await fetchWithCorsFallback(siteApiUrl);
        const siteRecords = transformApiData(siteData, false);
        setStockInSite(siteRecords);
        
        // Switch to site view to show the newly added data
        setSelectedOption('site');
        setCurrentPage(1); // Reset to first page
        
        setSuccessMessage('Stock In added successfully!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        handleClose();
        return;
      } catch (error) {
        console.error('Error creating stock in:', error);
        alert(`Failed to create stock in: ${error.message}`);
        return;
      }
    }
    
    // For Edit mode, call API to update
    if (isEditMode) {
      try {
        // Find the record being edited to get the API ID
        const recordToEdit = currentData.find(item => item.id === editingId);
        const editStockId = recordToEdit?.stockId || recordToEdit?.stockInId;
        if (!recordToEdit || !editStockId) {
          throw new Error('Record ID not found for update');
        }

        // Convert dropdown values to display labels
        const materialOptions = modalSelectedOption === 'plant' ? plantMaterial : siteMaterial;
        const materialLabel = formData.materialtype === 'others' 
          ? formData.customMaterialType 
          : (materialOptions.find(opt => opt.value === formData.materialtype)?.label || formData.materialtype);
        
        const siteCodeValue =
          modalSelectedOption === 'site'
            ? siteCodes.find((opt) => opt.value === formData.sitecode)?.label || formData.sitecode
            : '';

        // Format date to YYYY-MM-DD for API
        const apiDate = formData.date; // Already in YYYY-MM-DD format from date input

        // Prepare materialGrade array - use grade if provided, otherwise use '-'
        const materialGrade = formData.grade ? [formData.grade] : ['-'];

        // Prepare materialUnit array - handle tonnageCFT for Plant and Site mode
        let materialUnit;
        if (modalSelectedOption === 'plant' && (matchMat(formData.materialtype, 'MSAND', 'aggregate', 'cement', 'dust', 'bitman') || _plantApiHasTonnageCFT) && formData.tonnageCFT) {
          if (formData.tonnageCFT === 'Tonnage') {
            materialUnit = formData.netWeight ? [`${formData.netWeight} metric tons`] : [''];
          } else if (formData.tonnageCFT === 'CFT') {
            materialUnit = formData.quantity ? [normalizeCftQuantityForApi(formData.quantity)] : [''];
          } else if (formData.tonnageCFT === 'Bag' && matchMat(formData.materialtype, 'cement')) {
            materialUnit = formData.quantity ? [normalizeBagQuantityForApi(formData.quantity)] : [''];
          } else {
            materialUnit = [formData.qty || ''];
          }
        } else if (modalSelectedOption === 'site' && matchMat(formData.materialtype, 'steel') && formData.tonnageCFT === 'Tonnage') {
          materialUnit = formData.netWeight ? [`${formData.netWeight} metric tons`] : [''];
        } else if (modalSelectedOption === 'site' && (matchMat(formData.materialtype, 'aggregate', 'cement') || _siteApiHasTonnageCFT) && formData.tonnageCFT) {
          if (formData.tonnageCFT === 'Tonnage') {
            materialUnit = formData.netWeight ? [`${formData.netWeight} metric tons`] : [''];
          } else if (formData.tonnageCFT === 'CFT') {
            materialUnit = formData.quantity ? [normalizeCftQuantityForApi(formData.quantity)] : [''];
          } else if (formData.tonnageCFT === 'Bag' && matchMat(formData.materialtype, 'cement')) {
            materialUnit = formData.quantity ? [normalizeBagQuantityForApi(formData.quantity)] : [''];
          } else {
            materialUnit = [formData.qty || ''];
          }
        } else if (matchMat(formData.materialtype, 'wires') && formData.qtyUnit) {
          // For Wires, include sqmm if provided
          if (formData.sqmm) {
            materialUnit = [`${formData.qty} ${formData.qtyUnit} ${formData.sqmm} sq.mm`];
          } else {
            materialUnit = [`${formData.qty} ${formData.qtyUnit}`];
          }
        } else {
          // Regular case - combine qty and qtyUnit if qtyUnit exists
          materialUnit = formData.qtyUnit 
            ? [`${formData.qty} ${formData.qtyUnit}`]
            : [formData.qty || ''];
        }

        // For tonnageCFT CFT mode, use cftTotalAmount; otherwise use totalamount
        let totalAmount;
        if (modalSelectedOption === 'plant' && isPlantCftStyleWeight(formData)) {
          totalAmount = parseFloat(formData.cftTotalAmount) || 0;
        } else if (modalSelectedOption === 'site' && isSiteCftStyleWeight(formData)) {
          totalAmount = parseFloat(formData.cftTotalAmount) || 0;
        } else {
          totalAmount = parseFloat(formData.totalamount) || 0;
        }

        const isPlantCftStyleForPut =
          modalSelectedOption === 'plant' && isPlantCftStyleWeight(formData);
        const isSiteCftStyleForPut =
          modalSelectedOption === 'site' && isSiteCftStyleWeight(formData);
        const quantityForPut =
          isPlantCftStyleForPut || isSiteCftStyleForPut
            ? quantityNumericOnlyForApi(formData.quantity ?? '')
            : modalSelectedOption === 'site'
              ? (formData.quantity ?? formData.qty ?? '')
              : (formData.quantity ?? '');

        // Prepare request body matching API format (include weights / qty / rate like create)
        const requestBody = {
          stockInId: editStockId,
          plant: modalSelectedOption === 'plant' ? (formData.plant || defaultPlant) : '',
          gstNumber: formData.gstNumber || '',
          gstPercentage: gstPercentagePayload,
          invoiceNumber: formData.invoiceNumber || '',
          materialType: materialLabel,
          materialGrade: materialGrade,
          materialUnit: materialUnit,
          vendor: formData.vendor || '',
          totalAmount: totalAmount,
          supervisor: formData.supervisor || '',
          date: apiDate,
          stockType: modalSelectedOption === 'plant' ? 'plant' : 'site',
          siteCode: modalSelectedOption === 'site' ? siteCodeValue : '',
          deliveryChallan: formData.dc || '',
          selectedWeight: formData.tonnageCFT ?? '',
          grossWeight: formData.grossWeight ?? '',
          tareWeight: formData.tareWeight ?? '',
          netWeight: formData.netWeight ?? '',
          quantity: quantityForPut,
          rate: formData.rate ?? '',
          ...(modalSelectedOption === 'site'
            ? {
                selectUnitType: formData.tonnageCFT ?? formData.qtyUnit ?? '',
                sqmm: formData.sqmm ?? ''
              }
            : {})
        };

        const apiUrl = `${API_BASE_URL}/api/stockIn/stock`;
        
        const response = await fetch(apiUrl, {
          method: 'PUT',
          mode: 'cors',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        });

        if (!response.ok) {
          // Try to get error message from response
          let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
          try {
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
              const errorData = await response.json();
              errorMessage = errorData.message || errorMessage;
            } else {
              const errorText = await response.text();
              if (errorText) {
                errorMessage = errorText;
              }
            }
          } catch (parseError) {
            console.error('Error parsing error response:', parseError);
          }
          throw new Error(errorMessage);
        }

        // Safely parse response - check if response has content
        let responseData = null;
        try {
          const contentType = response.headers.get('content-type');
          const responseText = await response.text();
          
          if (responseText && contentType && contentType.includes('application/json')) {
            responseData = JSON.parse(responseText);
          } else if (responseText) {
            // If response is not JSON but has content, log it
            console.log('Update response (non-JSON):', responseText);
            responseData = { message: 'Update successful' };
          } else {
            // Empty response - still consider it success
            responseData = { message: 'Update successful' };
          }
        } catch (parseError) {
          console.error('Error parsing response:', parseError);
          // If parsing fails but status is OK, still consider it success
          responseData = { message: 'Update successful' };
        }
        
        console.log('Stock In updated successfully:', responseData);

        await fetchStockInData();

        // Reset to first page to show the updated record
        setCurrentPage(1);

        setSuccessMessage('Stock In updated successfully!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        handleClose();
        return;
      } catch (error) {
        console.error('Error updating stock in:', error);
        alert(`Failed to update stock in: ${error.message}`);
        return;
      }
    }
    
    handleClose();
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setIsEditMode(false);
    setEditingId(null);
    setModalSelectedOption('plant');
    setFormData({
      date: getTodayDate(),
      materialtype: '',
      customMaterialType: '',
      grade: '',
      qty: '',
      qtyUnit: '',
      sqmm: '',
      vendor: '',
      totalamount: '',
      withGST: '',
      supervisor: '',
      sitecode: '',
      dc: '',
      grossWeight: '',
      tareWeight: '',
      netWeight: '',
      tonnageCFT: '',
      rate: '',
      quantity: '',
      cftTotalAmount: '',
      plant: defaultPlant,
      gstNumber: '',
      gstPercentage: '',
      invoiceNumber: ''
    });
  };

  const handleEdit = (id) => {

    const record = currentData.find(item => item.id === id);
    console.log(record);
    if (record) {
      // Map the existing data to dropdown values
      const mappedMaterialType = mapToDropdownValue(record.materialtype, selectedOption === 'plant' ? plantMaterial : siteMaterial);
      const orig = record.originalData;
      const rawSiteCode = String(
        record.sitecode ||
          orig?.siteCode ||
          orig?.sitecode ||
          orig?.site_code ||
          ''
      ).trim();
      const siteMatch = rawSiteCode
        ? siteCodes.find(
            (opt) =>
              String(opt.label).toLowerCase() === rawSiteCode.toLowerCase() ||
              String(opt.value).toLowerCase() === rawSiteCode.toLowerCase()
          )
        : null;
      const sitecodeForForm = siteMatch ? siteMatch.value : '';

      // Check if the material type is a custom one (not in the predefined list)
      const isCustomMaterialType = record.materialtype && !(selectedOption === 'plant' ? plantMaterial : siteMaterial).find(opt => opt.label === record.materialtype);

      const detail0 = Array.isArray(orig?.details) ? orig.details[0] : null;
      const firstNonEmptyNumText = (...vals) => {
        for (const v of vals) {
          if (v == null) continue;
          const s = String(v).trim();
          if (s !== '' && s !== '-') return s;
        }
        return '';
      };

      const pickNumericFormField = (recordVal, origVal) => {
        if (recordVal !== '' && recordVal != null) return String(recordVal);
        if (origVal != null && origVal !== '') return String(origVal);
        return '';
      };
      const totalAmountForForm = pickNumericFormField(record.totalamount, orig?.totalAmount);
      const gstPctForForm =
        record.gstPercentage != null && record.gstPercentage !== ''
          ? String(record.gstPercentage).replace(/%$/, '').trim()
          : '';

      setFormData({
        date: record.date ? formatDateForInput(record.date) : '',
        materialtype: isCustomMaterialType ? 'others' : mappedMaterialType,
        customMaterialType: isCustomMaterialType ? record.materialtype : '',
        grade: record.grade || '',
        qty: record.qty || '',
        qtyUnit: record.qtyUnit || '',
        sqmm: record.sqmm || (orig && (orig.sqmm != null ? String(orig.sqmm) : '')) || '',
        vendor: record.vendor || '',
        totalamount: totalAmountForForm,
        withGST: record.withGST || '',
        supervisor: record.supervisor || '',
        sitecode: sitecodeForForm,
        dc: record.dc || '',
        grossWeight: firstNonEmptyNumText(
          record.grossWeight,
          detail0?.grossWeight,
          detail0?.gross_weight,
          orig?.grossWeight,
          orig?.gross_weight
        ),
        tareWeight: firstNonEmptyNumText(
          record.tareWeight,
          detail0?.tareWeight,
          detail0?.tare_weight,
          orig?.tareWeight,
          orig?.tare_weight
        ),
        netWeight: firstNonEmptyNumText(
          record.netWeight,
          detail0?.netWeight,
          detail0?.net_weight,
          orig?.netWeight,
          orig?.net_weight
        ),
        tonnageCFT: record.tonnageCFT || record.selectedWeight || '',
        rate: record.rate || '',
        quantity: quantityDisplayNoWeightUnit(
          (record.quantity && String(record.quantity).trim() !== '-'
            ? String(record.quantity).trim()
            : '') || record.qty || ''
        ),
        cftTotalAmount: totalAmountForForm,
        plant: record.plant || '',
        gstNumber: formatIndianGstNumber(record.gstNumber || ''),
        gstPercentage: gstPctForForm,
        invoiceNumber: record.invoiceNumber || ''
      });
      setModalSelectedOption(selectedOption);
      setEditingId(id);
      setIsEditMode(true);
      setIsModalOpen(true);
    }
  };

  // Helper function to map display values to dropdown values
  const mapToDropdownValue = (displayValue, options) => {
    if (!displayValue) return '';
    const option = options.find(opt => 
      opt.label.toLowerCase() === displayValue.toLowerCase() || 
      opt.value.toLowerCase() === displayValue.toLowerCase()
    );
    return option ? option.value : displayValue;
  };

  const handleDelete = (id) => {
    setRecordToDelete(id);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!recordToDelete) return;
    
    try {
      // Find the record to get the API ID
      const record = currentData.find(item => item.id === recordToDelete);
      if (!record || !record.stockId) {
        throw new Error('Record ID not found');
      }

      const apiUrl = `${API_BASE_URL}/api/stockIn/stockIn/${record.stockId}`;
      
      const response = await fetch(apiUrl, {
        method: 'DELETE',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      await fetchStockInData();

      setSuccessMessage('Stock In record deleted successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setShowDeleteConfirm(false);
      setRecordToDelete(null);
    } catch (error) {
      console.error('Error deleting stock in record:', error);
      setError(error.message || 'Failed to delete stock in record');
      setSuccessMessage('Failed to delete stock in record');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      setShowDeleteConfirm(false);
      setRecordToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setRecordToDelete(null);
  };

  const handleCloseSnackbar = (event, reason) => {
    setSnackbarOpen(false);
    setSuccessMessage('');
  };



  return (
    <>
      {/* Success Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        style={{position: 'absolute', bottom: '10px', left: '10px'}}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbarSeverity}
          sx={{ 
            width: '100%', 
            backgroundColor: snackbarSeverity === 'success' ? '#43a047' : '#dc2626', 
            color: '#fff', 
            fontWeight: '400',
            fontSize: '18px' 
          }}
        >
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Error Message */}
      {error && (
        <div className="bg-red-200 border border-red-400 text-red-700 px-4 py-3 rounded" style={{position: 'absolute', bottom: '10px', left: '10px', zIndex: '1000', minWidth: '200px'}}>
          {error}
        </div>
      )}
      
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-6">
          <h1 className="text-2xl font-bold text-gray-900 font-poppins">Stock In</h1>
          <div className="flex items-center space-x-4">
            <label className="flex items-center">
              <input
                type="radio"
                name="dataType"
                value="plant"
                checked={selectedOption === 'plant'}
                onChange={(e) => setSelectedOption(e.target.value)}
                className="mr-2"
              />
              <span className="text-sm font-medium text-gray-700">Plant</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="dataType"
                value="site"
                checked={selectedOption === 'site'}
                onChange={(e) => setSelectedOption(e.target.value)}
                className="mr-2"
              />
              <span className="text-sm font-medium text-gray-700">Site</span>
            </label>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <button
            type="button"
            onClick={fetchStockInData}
            className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            title="Refresh"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v6h6M20 20v-6h-6M20 8a8 8 0 00-14.828-3M4 16a8 8 0 0014.828 3" />
            </svg>
           
          </button>
          {/* Column Visibility Toggle */}
          <div className="relative column-menu-container">
            <button 
              onClick={() => setShowColumnMenu(!showColumnMenu)}
              className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              title="Toggle Columns"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            {showColumnMenu && (
              <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-96 overflow-y-auto">
                <div className="py-1">
                  <div className="px-3 py-2 border-b border-gray-200">
                    <button
                      type="button"
                      onClick={handleResetColumns}
                      className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Reset
                    </button>
                  </div>
                  {columnDefinitions.map(col => (
                    <label key={col.key} className="flex items-center px-4 py-2 hover:bg-gray-100 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={visibleColumns[col.key]}
                        onChange={() => toggleColumn(col.key)}
                        disabled={visibleColumns[col.key] && getVisibleColumnCount() === 1}
                        className="mr-3"
                      />
                      <span className="text-sm text-gray-700">{col.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          {isAdministrator && (
            <button 
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              title={showFilters ? "Hide Filters" : "Show Filters"}
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z" />
              </svg>
              <span>{showFilters ? "Hide Filters" : "Show Filters"}</span>
            </button>
          )}
          <button 
            onClick={() => {
              setIsEditMode(false);
              // Set default plant value and current date when opening add modal
              setFormData(prev => ({
                ...prev,
                plant: defaultPlant,
                date: getTodayDate(),
                gstNumber: '',
                gstPercentage: '',
                invoiceNumber: ''
              }));
              setIsModalOpen(true);
            }}
            className="btn-primary"
          >
            Add Stock In
          </button>
        </div>
      </div>

      {isAdministrator && showFilters && (
            <div className="p-4 flex flex-wrap items-end gap-4 bg-white shadow rounded-lg">
              <div className="flex flex-col">
              <label className="text-xs text-gray-600 mb-1">
                {datePreset == '' ? 'Choose Date' : 'Selected Date Range: '} 
                {datePreset && <span className="font-bold text-black text-xs uppercase">({datePreset == "today" ? "Today" : datePreset == "yesterday" ? "Yesterday" : datePreset == "last7" ? "Last 7 Days" : datePreset == "last30" ? "Last 30 Days" : datePreset == "thisMonth" ? "This Month" : datePreset == "lastMonth" ? "Last Month" : datePreset == "custom" ? "Custom Range" : datePreset})</span>}
              </label>
              <div className="relative date-dropdown-container">
                <div className="relative">
                  <input
                    type="text"
                    value={dateDisplay || 'Select Range'}
                    readOnly
                    onClick={() => setShowDateDropdown(!showDateDropdown)}
                    className="w-full px-3 py-2 pl-10 pr-8 border border-gray-300 rounded-md text-sm bg-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Select Date Range"
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  {dateDisplay && (
                    <button
                      type="button"
                      onClick={handleClearDate}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center hover:bg-gray-100 rounded-r-md"
                      title="Clear date"
                    >
                      <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
                {showDateDropdown && (
                  <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg z-20">
                    <div className="py-1">
                      <button
                        onClick={() => handleDatePresetChange('today')}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 ${datePreset === 'today' ? 'bg-orange-100 text-orange-600' : 'text-gray-700'}`}
                      >
                        Today
                      </button>
                      <button
                        onClick={() => handleDatePresetChange('yesterday')}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 ${datePreset === 'yesterday' ? 'bg-orange-100 text-orange-600' : 'text-gray-700'}`}
                      >
                        Yesterday
                      </button>
                      <button
                        onClick={() => handleDatePresetChange('last7')}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 ${datePreset === 'last7' ? 'bg-orange-100 text-orange-600' : 'text-gray-700'}`}
                      >
                        Last 7 Days
                      </button>
                      <button
                        onClick={() => handleDatePresetChange('last30')}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 ${datePreset === 'last30' ? 'bg-orange-100 text-orange-600' : 'text-gray-700'}`}
                      >
                        Last 30 Days
                      </button>
                      <button
                        onClick={() => handleDatePresetChange('thisMonth')}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 ${datePreset === 'thisMonth' ? 'bg-orange-100 text-orange-600' : 'text-gray-700'}`}
                      >
                        This Month
                      </button>
                      <button
                        onClick={() => handleDatePresetChange('lastMonth')}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 ${datePreset === 'lastMonth' ? 'bg-orange-100 text-orange-600' : 'text-gray-700'}`}
                      >
                        Last Month
                      </button>
                      <button
                        onClick={() => handleDatePresetChange('custom')}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 ${datePreset === 'custom' ? 'bg-orange-100 text-orange-600' : 'text-gray-700'}`}
                      >
                        Custom Range
                      </button>
                    </div>
                  </div>
                )}
                {/* Custom Calendar Modal */}
                {showCustomCalendar && (
                  <div className="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-30 p-4">
                    <div className="flex">
                      {/* Left side - Preset options */}
                      <div className="w-48 pr-4 border-r border-gray-200">
                        <div className="space-y-1">
                          <button
                            onClick={() => handleDatePresetChange('today')}
                            className={`w-full text-left px-3 py-2 text-sm rounded ${datePreset === 'today' ? 'bg-orange-100 text-orange-600' : 'text-gray-700 hover:bg-gray-100'}`}
                          >
                            Today
                          </button>
                          <button
                            onClick={() => handleDatePresetChange('yesterday')}
                            className={`w-full text-left px-3 py-2 text-sm rounded ${datePreset === 'yesterday' ? 'bg-orange-100 text-orange-600' : 'text-gray-700 hover:bg-gray-100'}`}
                          >
                            Yesterday
                          </button>
                          <button
                            onClick={() => handleDatePresetChange('last7')}
                            className={`w-full text-left px-3 py-2 text-sm rounded ${datePreset === 'last7' ? 'bg-orange-100 text-orange-600' : 'text-gray-700 hover:bg-gray-100'}`}
                          >
                            Last 7 Days
                          </button>
                          <button
                            onClick={() => handleDatePresetChange('last30')}
                            className={`w-full text-left px-3 py-2 text-sm rounded ${datePreset === 'last30' ? 'bg-orange-100 text-orange-600' : 'text-gray-700 hover:bg-gray-100'}`}
                          >
                            Last 30 Days
                          </button>
                          <button
                            onClick={() => handleDatePresetChange('thisMonth')}
                            className={`w-full text-left px-3 py-2 text-sm rounded ${datePreset === 'thisMonth' ? 'bg-orange-100 text-orange-600' : 'text-gray-700 hover:bg-gray-100'}`}
                          >
                            This Month
                          </button>
                          <button
                            onClick={() => handleDatePresetChange('lastMonth')}
                            className={`w-full text-left px-3 py-2 text-sm rounded ${datePreset === 'lastMonth' ? 'bg-orange-100 text-orange-600' : 'text-gray-700 hover:bg-gray-100'}`}
                          >
                            Last Month
                          </button>
                          <button
                            onClick={() => handleDatePresetChange('custom')}
                            className={`w-full text-left px-3 py-2 text-sm rounded ${datePreset === 'custom' ? 'bg-orange-100 text-orange-600' : 'text-gray-700 hover:bg-gray-100'}`}
                          >
                            Custom Range
                          </button>
                        </div>
                      </div>
                      
                      {/* Right side - Calendar */}
                      <div className="ml-4">
                        {/* Calendar Navigation */}
                        <div className="flex items-center justify-between mb-4">
                          <button
                            onClick={() => navigateMonth(-1)}
                            className="p-1 hover:bg-gray-100 rounded"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                          </button>
                          <h3 className="text-sm font-medium text-gray-900">
                            {currentMonth.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                          </h3>
                          <button
                            onClick={() => navigateMonth(1)}
                            className="p-1 hover:bg-gray-100 rounded"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                        </div>
                        
                        {/* Calendar Grid */}
                        <div className="w-64">
                          {/* Days of week header */}
                          <div className="grid grid-cols-7 gap-1 mb-2">
                            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                              <div key={day} className="text-xs text-gray-500 text-center py-1">
                                {day}
                              </div>
                            ))}
                          </div>
                          
                          {/* Calendar days */}
                          <div className="grid grid-cols-7 gap-1">
                            {(() => {
                              const daysInMonth = getDaysInMonth(currentMonth);
                              const firstDay = getFirstDayOfMonth(currentMonth);
                              const days = [];
                              
                              // Previous month days
                              const prevMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 0);
                              for (let i = firstDay - 1; i >= 0; i--) {
                                const day = prevMonth.getDate() - i;
                                days.push(
                                  <button
                                    key={`prev-${day}`}
                                    className="text-xs text-gray-400 py-1 hover:bg-gray-100 rounded"
                                    disabled
                                  >
                                    {day}
                                  </button>
                                );
                              }
                              
                              // Current month days
                              for (let day = 1; day <= daysInMonth; day++) {
                                const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
                                const isSelected = isSameDate(date, customStartDate) || isSameDate(date, customEndDate);
                                const isInRange = customStartDate && customEndDate && isDateInRange(date, customStartDate, customEndDate);
                                
                                days.push(
                                  <button
                                    key={day}
                                    onClick={() => handleDateClick(date)}
                                    className={`text-xs py-1 rounded hover:bg-gray-100 ${
                                      isSelected 
                                        ? 'bg-orange-500 text-white' 
                                        : isInRange 
                                        ? 'bg-orange-100 text-orange-600' 
                                        : 'text-gray-700'
                                    }`}
                                  >
                                    {day}
                                  </button>
                                );
                              }
                              
                              // Next month days
                              const remainingCells = 42 - days.length; // 6 rows * 7 days
                              for (let day = 1; day <= remainingCells; day++) {
                                days.push(
                                  <button
                                    key={`next-${day}`}
                                    className="text-xs text-gray-400 py-1 hover:bg-gray-100 rounded"
                                    disabled
                                  >
                                    {day}
                                  </button>
                                );
                              }
                              
                              return days;
                            })()}
                          </div>
                        </div>
                        
                        {/* Selected date range display */}
                        <div className="mt-4 text-center">
                          <div className="text-sm text-gray-600 mb-3">
                            {customStartDate && customEndDate 
                              ? `${formatDate(customStartDate)} - ${formatDate(customEndDate)}`
                              : customStartDate 
                              ? `${formatDate(customStartDate)} - Select end date`
                              : 'Select start date'
                            }
                          </div>
                          
                          {/* Action buttons */}
                          <div className="flex gap-2 justify-center">
                            <button
                              onClick={handleCancelCustomRange}
                              className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                            >
                              Close
                            </button>
                            <button
                              onClick={handleApplyCustomRange}
                              disabled={!customStartDate || !customEndDate}
                              className="px-4 py-2 text-sm bg-orange-500 text-white rounded-md hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                            >
                              Apply
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              </div>
              {selectedOption === 'site' && (
                <div className="flex flex-col max-w-[120px]">
                <label className="text-xs text-gray-600 mb-1">Site</label>
                <div className="relative store-autocomplete-container">
                  <input
                    type="text"
                    value={storeInputValue}
                    onChange={handleStoreInputChange}
                    onFocus={() => {
                      setShowStoreDropdown(true);
                    }}
                    placeholder="Search"
                    className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    autoComplete="off"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center">
                    {storeInputValue ? (
                      <button
                        type="button"
                        onClick={handleClearStore}
                        className="pr-3 hover:bg-gray-100 rounded-r-md flex items-center"
                        title="Clear store"
                      >
                        <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    ) : (
                      <div className="pr-3 pointer-events-none">
                        <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      </div>
                    )}
                  </div>
                  {showStoreDropdown && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
                      <div className="py-1">
                        {filteredStoreOptions.length > 0 ? (
                          filteredStoreOptions.map(opt => (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => handleStoreSelect(opt)}
                              className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 focus:bg-orange-100 focus:outline-none ${
                                selectedStore === opt.value ? 'bg-orange-100 text-orange-600 font-medium' : 'text-gray-700'
                              }`}
                            >
                              {opt.label}
                            </button>
                          ))
                        ) : (
                          <div className="px-3 py-2 text-sm text-gray-500 text-center">
                            No matches found
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              )}
              <div className="flex flex-col max-w-[130px]">
              <label className="text-xs text-gray-600 mb-1">Material Type</label>
              <div className="relative product-autocomplete-container">
                <input
                  type="text"
                  value={productInputValue}
                  onChange={handleProductInputChange}
                  onFocus={() => {
                    setShowProductDropdown(true);
                  }}
                  placeholder="Search"
                  className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  autoComplete="off"
                />
                <div className="absolute inset-y-0 right-0 flex items-center">
                  {productInputValue ? (
                    <button
                      type="button"
                      onClick={handleClearProduct}
                      className="pr-3 hover:bg-gray-100 rounded-r-md flex items-center"
                      title="Clear material type"
                    >
                      <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  ) : (
                    <div className="pr-3 pointer-events-none">
                      <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                  )}
                </div>
                {showProductDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
                    <div className="py-1">
                      {filteredProductOptions.length > 0 ? (
                        filteredProductOptions.map(opt => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => handleProductSelect(opt)}
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 focus:bg-orange-100 focus:outline-none ${
                              selectedProduct === opt.value ? 'bg-orange-100 text-orange-600 font-medium' : 'text-gray-700'
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))
                      ) : (
                        <div className="px-3 py-2 text-sm text-gray-500 text-center">
                          No matches found
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              </div>
              
              <div className="flex flex-col max-w-[120px]">
                <label className="text-xs text-gray-600 mb-1">Vendor</label>
                <div className="relative vendor-autocomplete-container">
                  <input
                    type="text"
                    value={vendorInputValue}
                    onChange={handleVendorInputChange}
                    placeholder="Search"
                    className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    autoComplete="off"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center">
                    {vendorInputValue ? (
                      <button
                        type="button"
                        onClick={handleClearVendor}
                        className="pr-3 hover:bg-gray-100 rounded-r-md flex items-center"
                        title="Clear vendor"
                      >
                        <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    ) : (
                      <div className="pr-3 pointer-events-none">
                        <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="flex flex-col max-w-[120px]">
              <label className="text-xs text-gray-600 mb-1">Supervisor</label>
              <div className="relative supervisor-autocomplete-container">
                <input
                  type="text"
                  value={supervisorInputValue}
                  onChange={handleSupervisorInputChange}
                  onFocus={() => {
                    setShowSupervisorDropdown(true);
                  }}
                    placeholder="Search"
                  className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  autoComplete="off"
                />
                <div className="absolute inset-y-0 right-0 flex items-center">
                  {supervisorInputValue ? (
                    <button
                      type="button"
                      onClick={handleClearSupervisor}
                      className="pr-3 hover:bg-gray-100 rounded-r-md flex items-center"
                      title="Clear supervisor"
                    >
                      <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  ) : (
                    <div className="pr-3 pointer-events-none">
                      <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                  )}
                </div>
                {showSupervisorDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
                    <div className="py-1">
                      {filteredSupervisorOptions.length > 0 ? (
                        filteredSupervisorOptions.map(opt => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => handleSupervisorSelect(opt)}
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 focus:bg-orange-100 focus:outline-none ${
                              selectedSupervisor === opt.value ? 'bg-orange-100 text-orange-600 font-medium' : 'text-gray-700'
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))
                      ) : (
                        <div className="px-3 py-2 text-sm text-gray-500 text-center">
                          No matches found
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              </div>
              
              <div className="ml-auto flex items-center gap-2">
              <button 
                onClick={handleClearFilters}
                disabled={!hasActiveFilters}
                className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
                title="Clear All Filters"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span>Clear All</span>
              </button>
              <button 
                onClick={handleExportPDF}
                className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                title="Export as PDF"
              >
                <img src="/assets/img/pdf.png" alt="PDF" className="h-4 w-4" />
              </button>
              <button 
                onClick={handleExportExcel}
                className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                title="Export as Excel"
              >
                <img src="/assets/img/sheet.png" alt="Excel" className="h-4 w-4" />
              </button>
              </div>
            </div>
          )}

      {isLoading ? (
        <div className="bg-white shadow rounded-lg p-8 flex items-center justify-center min-h-[400px]">
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
            <p className="text-gray-600 text-sm">Loading data...</p>
          </div>
        </div>
      ) : (
        <Grid  className="p-0 flex flex-wrap items-end rounded-lg" style={{marginTop: '10px'}}>
          <div className="">
            <div className="overflow-x-auto">
              <div 
                className={`relative overflow-y-auto ${!showFilters ? 'bg-white' : ''}`}
                style={{
                  height: `max(320px, calc(100vh - ${isAdministrator ? 370 : 270}px))`,
                  backgroundColor: '#fff',
                }}
              >
                <table className="min-w-full divide-y divide-gray-200 shadow rounded-lg border border-gray-200">
                  <thead className="sticky top-0 z-10" style={{backgroundColor: "rgb(0, 150, 136)"}}>
                    <tr>
                      {visibleColumns.date && (
                        <th 
                          className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                          style={{backgroundColor: "rgb(0, 150, 136)"}}
                          onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                          onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                          onClick={() => handleSort('date')}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Date</span>
                            {getSortIcon('date')}
                          </div>
                        </th>
                      )}
                      {selectedOption === 'plant' && visibleColumns.plant && (
                        <th
                          className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                          style={{ backgroundColor: 'rgb(0, 150, 136)' }}
                          onMouseEnter={(e) => (e.target.style.backgroundColor = 'rgb(0, 130, 120)')}
                          onMouseLeave={(e) => (e.target.style.backgroundColor = 'rgb(0, 150, 136)')}
                          onClick={() => handleSort('plant')}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Plant</span>
                            {getSortIcon('plant')}
                          </div>
                        </th>
                      )}
                      {selectedOption === 'plant' && visibleColumns.invoiceNumber && (
                        <th
                          className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                          style={{ backgroundColor: 'rgb(0, 150, 136)' }}
                          onMouseEnter={(e) => (e.target.style.backgroundColor = 'rgb(0, 130, 120)')}
                          onMouseLeave={(e) => (e.target.style.backgroundColor = 'rgb(0, 150, 136)')}
                          onClick={() => handleSort('invoiceNumber')}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Invoice No.</span>
                            {getSortIcon('invoiceNumber')}
                          </div>
                        </th>
                      )}
                      {selectedOption === 'site' && visibleColumns.sitecode && (
                        <th 
                          className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                          style={{backgroundColor: "rgb(0, 150, 136)"}}
                          onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                          onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                          onClick={() => handleSort('sitecode')}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Site Code</span>
                            {getSortIcon('sitecode')}
                          </div>
                        </th>
                      )}
                      {selectedOption === 'site' && visibleColumns.invoiceNumber && (
                        <th
                          className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                          style={{ backgroundColor: 'rgb(0, 150, 136)' }}
                          onMouseEnter={(e) => (e.target.style.backgroundColor = 'rgb(0, 130, 120)')}
                          onMouseLeave={(e) => (e.target.style.backgroundColor = 'rgb(0, 150, 136)')}
                          onClick={() => handleSort('invoiceNumber')}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Invoice No.</span>
                            {getSortIcon('invoiceNumber')}
                          </div>
                        </th>
                      )}
                     
                      {visibleColumns.dc && (
                        <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-white" style={{backgroundColor: "rgb(0, 150, 136)"}}>DC (Delivery Challan)</th>
                      )}
                      {visibleColumns.materialtype && (
                        <th 
                          className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                          style={{backgroundColor: "rgb(0, 150, 136)"}}
                          onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                          onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                          onClick={() => handleSort('materialtype')}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Material Type</span>
                            {getSortIcon('materialtype')}
                          </div>
                        </th>
                      )}
                      {visibleColumns.grade && (
                        <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-white" style={{backgroundColor: "rgb(0, 150, 136)"}}>Grade</th>
                      )}
                       {visibleColumns.weight && (
                        <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-white" style={{backgroundColor: "rgb(0, 150, 136)"}}>Weight</th>
                      )}
                      {visibleColumns.grossWeight && (
                        <th className="px-6 py-3 text-right text-xs font-medium tracking-wider text-white" style={{backgroundColor: "rgb(0, 150, 136)"}}>Gross Weight</th>
                      )}
                      {visibleColumns.tareWeight && (
                        <th className="px-6 py-3 text-right text-xs font-medium tracking-wider text-white" style={{backgroundColor: "rgb(0, 150, 136)"}}>Tare Weight</th>
                      )}
                      {visibleColumns.netWeight && (
                        <th className="px-6 py-3 text-right text-xs font-medium tracking-wider text-white" style={{backgroundColor: "rgb(0, 150, 136)"}}>Net Weight</th>
                      )}
                        {visibleColumns.rate && (
                        <th className="px-6 py-3 text-right text-xs font-medium tracking-wider text-white" style={{backgroundColor: "rgb(0, 150, 136)"}}>Rate</th>
                      )}
                      {visibleColumns.quantity && (
                        <th className="px-6 py-3 text-right text-xs font-medium tracking-wider text-white" style={{backgroundColor: "rgb(0, 150, 136)"}}>Quantity</th>
                      )}
                    
                     
                       {visibleColumns.totalamount && (
                        <th 
                          className="px-6 py-3 text-right text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                          style={{backgroundColor: "rgb(0, 150, 136)"}}
                          onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                          onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                          onClick={() => handleSort('totalamount')}
                        >
                          <div className="flex items-center justify-end space-x-1">
                            <span>Total Amount</span>
                            {getSortIcon('totalamount')}
                          </div>
                        </th>
                      )}
                      {/* {visibleColumns.qty && (
                        <th 
                          className="px-6 py-3 text-right text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                          style={{backgroundColor: "rgb(0, 150, 136)"}}
                          onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                          onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                          onClick={() => handleSort('qty')}
                        >
                          <div className="flex items-center justify-end space-x-1">
                            <span>Quantity</span>
                            {getSortIcon('qty')}
                          </div>
                        </th>
                      )} */}
                      {visibleColumns.gstPercentage && (
                        <th
                          className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                          style={{ backgroundColor: 'rgb(0, 150, 136)' }}
                          onMouseEnter={(e) => (e.target.style.backgroundColor = 'rgb(0, 130, 120)')}
                          onMouseLeave={(e) => (e.target.style.backgroundColor = 'rgb(0, 150, 136)')}
                          onClick={() => handleSort('gstPercentage')}
                        >
                          <div className="flex items-center space-x-1">
                            <span>GST(%)</span>
                            {getSortIcon('gstPercentage')}
                          </div>
                        </th>
                      )}
                     
                      {visibleColumns.withGST && (
                        <th 
                          className="px-6 py-3 text-right text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                          style={{backgroundColor: "rgb(0, 150, 136)"}}
                          onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                          onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                          onClick={() => handleSort('withGST')}
                        >
                          <div className="flex items-center justify-end space-x-1">
                            <span>With GST</span>
                            {getSortIcon('withGST')}
                          </div>
                        </th>
                      )}
                      {visibleColumns.gstNumber && (
                        <th
                          className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                          style={{ backgroundColor: 'rgb(0, 150, 136)' }}
                          onMouseEnter={(e) => (e.target.style.backgroundColor = 'rgb(0, 130, 120)')}
                          onMouseLeave={(e) => (e.target.style.backgroundColor = 'rgb(0, 150, 136)')}
                          onClick={() => handleSort('gstNumber')}
                        >
                          <div className="flex items-center space-x-1">
                            <span>GST Number</span>
                            {getSortIcon('gstNumber')}
                          </div>
                        </th>
                      )}
                      {visibleColumns.vendor && (
                        <th 
                          className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                          style={{backgroundColor: "rgb(0, 150, 136)"}}
                          onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                          onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                          onClick={() => handleSort('vendor')}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Vendor</span>
                            {getSortIcon('vendor')}
                          </div>
                        </th>
                      )}
                      {visibleColumns.supervisor && (
                        <th 
                          className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                          style={{backgroundColor: "rgb(0, 150, 136)"}}
                          onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                          onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                          onClick={() => handleSort('supervisor')}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Supervisor</span>
                            {getSortIcon('supervisor')}
                          </div>
                        </th>
                      )}
                      {visibleColumns.actions && (
                        <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-white" style={{backgroundColor: "rgb(0, 150, 136)"}}>Actions</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {!isLoading && paginatedData.length === 0 ? (
                      <tr>
                        <td colSpan={getVisibleColumnCount()} className="px-6 py-8 text-center text-sm text-gray-500">
                          No stock in records found.
                        </td>
                      </tr>
                    ) : (
                      paginatedData.map((record) => (
                        <tr key={record.id}>
                          {visibleColumns.date && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{record.date}</td>
                          )}
                          {selectedOption === 'plant' && visibleColumns.plant && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{record.plant || '—'}</td>
                          )}
                          {selectedOption === 'plant' && visibleColumns.invoiceNumber && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {record.invoiceNumber || '—'}
                            </td>
                          )}
                          {selectedOption === 'site' && visibleColumns.sitecode && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{record.sitecode}</td>
                          )}
                          {selectedOption === 'site' && visibleColumns.invoiceNumber && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {record.invoiceNumber || '—'}
                            </td>
                          )}
                        
                          {visibleColumns.dc && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{record.dc || '-'}</td>
                          )}
                          {visibleColumns.materialtype && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{record.materialtype}</td>
                          )}
                          {visibleColumns.grade && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm">{record.grade}</td>
                          )}
                            {visibleColumns.weight && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm">{record.selectedWeight || '-'}</td>
                          )}
                          {visibleColumns.grossWeight && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-right">{record.grossWeight || '-'}</td>
                          )}
                          {visibleColumns.tareWeight && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-right">{record.tareWeight || '-'}</td>
                          )}
                          {visibleColumns.netWeight && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-right">{record.netWeight || '-'}</td>
                          )}
                           {visibleColumns.rate && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-right">{record.rate || '-'}</td>
                          )}
                          {visibleColumns.quantity && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-right">{formatQuantityColumn3(record.quantity) || '-'}</td>
                          )}
                         
                           {visibleColumns.totalamount && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-right">{record.totalamount}</td>
                          )}
                        
                          {/* {visibleColumns.qty && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-right">{record.qty}{record.qtyUnit ? ` (${record.qtyUnit})` : ''}</td>
                          )} */}
                          {visibleColumns.gstPercentage && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {record.gstPercentage != null && record.gstPercentage !== ''
                                ? `${String(record.gstPercentage).replace(/%$/, '').trim()}%`
                                : '—'}
                            </td>
                          )}
                         
                          {visibleColumns.withGST && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-right">{record.withGST || '-'}</td>
                          )}
                          {visibleColumns.gstNumber && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-mono">
                              {record.gstNumber || '—'}
                            </td>
                          )}
                          {visibleColumns.vendor && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm">{record.vendor}</td>
                          )}
                          {visibleColumns.supervisor && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm">{record.supervisor}</td>
                          )}
                          {visibleColumns.actions && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => handleEdit(record.id)}
                                  className="text-indigo-600 hover:text-indigo-900 focus:outline-none focus:underline"
                                  title="Edit"
                                >
                                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                </button>
                                <button
                                  onClick={() => handleDelete(record.id)}
                                  className="text-red-600 hover:text-red-900 focus:outline-none focus:underline"
                                  title="Delete"
                                >
                                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </div>
                            </td>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        
          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            itemsPerPage={itemsPerPage}
            totalItems={filteredData.length}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        </Grid>
      )}

      {/* Add/Edit Stock In Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 m-0 overflow-hidden" style={{marginTop: 0}}>
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div 
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={handleClose}
            ></div>

            {/* Modal panel */}
            <div
              className="inline-block align-bottom bg-white rounded-lg text-left shadow-xl transform transition-all modal-card stock-in-modal-panel"
            >
              <form onSubmit={handleSubmit} className="flex h-full min-h-0 flex-col">
                {/* Header */}
                <div className="flex flex-shrink-0 items-center justify-between border-b border-gray-200 bg-white px-6 py-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    {isEditMode ? 'Edit Stock In' : 'Add Stock In'}
                  </h3>
                  <button
                    type="button"
                    onClick={handleClose}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Form content */}
                <div className="min-h-0 flex-1 overflow-y-auto bg-white px-6 py-4">
                  {/* 1. Location & Date */}
                  <ModalSection>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                          Stock In (Plant / Site) <span className="text-red-500">*</span>
                        </label>
                        <div className="flex items-center space-x-6">
                          <label className={`flex items-center ${isEditMode ? 'cursor-not-allowed opacity-60' : ''}`}>
                            <input
                              type="radio"
                              name="modalDataType"
                              value="plant"
                              checked={modalSelectedOption === 'plant'}
                              onChange={handleModalOptionChange}
                              disabled={isEditMode}
                              readOnly={true}
                              className="mr-2"
                            />
                            <span className="text-sm font-medium text-gray-700">Plant</span>
                          </label>
                          <label className={`flex items-center ${isEditMode ? 'cursor-not-allowed opacity-60' : ''}`}>
                            <input
                              type="radio"
                              name="modalDataType"
                              value="site"
                              checked={modalSelectedOption === 'site'}
                              onChange={handleModalOptionChange}
                              disabled={isEditMode}
                              className="mr-2"
                            />
                            <span className="text-sm font-medium text-gray-700">Site</span>
                          </label>
                        </div>
                      </div>

                      {modalSelectedOption === 'plant' ? (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Plant <span className="text-red-500">*</span>
                          </label>
                          <select
                            name="plant"
                            value={formData.plant || defaultPlant}
                            onChange={handleInputChange}
                            required={modalSelectedOption === 'plant'}
                            disabled={isEditMode}
                            className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                              isEditMode ? 'bg-gray-100 text-gray-600 cursor-not-allowed' : ''
                            }`}
                          >
                            {maintenancePlantOptions.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      ) : (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Site Code <span className="text-red-500">*</span>
                          </label>
                          <select
                            name="sitecode"
                            value={formData.sitecode}
                            onChange={handleInputChange}
                            required={modalSelectedOption === 'site'}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          >
                            <option value="">Select</option>
                            {siteCodes.map((option) => (
                              <option
                                key={option.id != null ? `maint-site-${option.id}` : option.value}
                                value={option.value}
                              >
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Date <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="date"
                          name="date"
                          value={formData.date}
                          onChange={handleInputChange}
                          max={getTodayDate()}
                          required
                          readOnly={isEditMode}
                          disabled={isEditMode}
                          className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                            isEditMode ? 'bg-gray-100 text-gray-600 cursor-not-allowed' : ''
                          }`}
                        />
                      </div>
                    </div>
                  </ModalSection>

                  {/* 2. Reference Details */}
                  <ModalSection>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          DC (Delivery Challan) <span className="text-red-500">*</span>
                        </label>
                        <input
                          required
                          type="text"
                          name="dc"
                          value={formData.dc}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          placeholder=""
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Invoice No.
                        </label>
                        <input
                          type="text"
                          name="invoiceNumber"
                          value={formData.invoiceNumber || ''}
                          onChange={handleInputChange}
                          placeholder=""
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          GST Number
                        </label>
                        <input
                          type="text"
                          name="gstNumber"
                          value={formData.gstNumber || ''}
                          onChange={handleInputChange}
                          maxLength={15}
                          placeholder=""
                          autoComplete="off"
                          spellCheck={false}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent font-mono uppercase"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          GST (%) 
                        </label>
                        <input
                          type="text"
                          name="gstPercentage"
                          value={formData.gstPercentage || ''}
                          onChange={handleInputChange}
                          placeholder=""
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                      </div>
                    </div>
                  </ModalSection>

                  {/* 3. Material Details */}
                  <ModalSection>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-4">
                    {/* Material Type - using grid-cols-3 */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Material Type <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="materialtype"
                        value={formData.materialtype}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      >
                        <option value="">Select </option>
                        {(modalSelectedOption === 'plant' ? plantMaterial : siteMaterial).map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                 

                    {/* Grade (MM) or Add Material Type - dynamic columns */}
                    {formData.materialtype && (
                      <>
                        {formData.materialtype === 'others' ? (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Add Material Type <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              name="customMaterialType"
                              value={formData.customMaterialType}
                              onChange={handleInputChange}
                              placeholder=""
                              required={formData.materialtype === 'others'}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            />
                          </div>
                        ) : (() => {
                            const _apiMat = getApiMaterialData(formData.materialtype, modalSelectedOption);
                            const _apiGrades = extractApiGrades(_apiMat);
                            const _showHardcoded =
                              (modalSelectedOption === 'plant' && matchMat(formData.materialtype, 'MSAND', 'aggregate', 'cement')) ||
                              (modalSelectedOption === 'site' && matchMat(formData.materialtype, 'steel', 'aggregate', 'cement', 'pvcpipes', 'wires', 'frpmanualdoor', 'frpcatchpit', 'ci', 'frprwh', 'rmc', 'bt'));
                            if (_apiGrades.length === 0 && !_showHardcoded) return null;
                            return (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Grade (MM) <span className="text-red-500">*</span>
                            </label>
                          {_apiGrades.length > 0 ? (
                            <select
                              name="grade"
                              value={formData.grade}
                              onChange={handleInputChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            >
                              <option value="">Select Grade</option>
                              {_apiGrades.map((g) => (
                                <option key={g} value={g}>{g}</option>
                              ))}
                            </select>
                          ) : modalSelectedOption === 'plant' && matchMat(formData.materialtype, 'MSAND') ? (
                            <select
                              name="grade"
                              value={formData.grade}
                              onChange={handleInputChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            >
                              <option value="">Select Grade</option>
                              <option value="Nice">Nice</option>
                              <option value="Normal">Normal</option>
                            </select>
                          ) : modalSelectedOption === 'plant' && matchMat(formData.materialtype, 'aggregate') ? (
                            <select
                              name="grade"
                              value={formData.grade}
                              onChange={handleInputChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            >
                              <option value="">Select Grade</option>
                              <option value="Chips">Chips</option>
                              <option value="6MM">6MM</option>
                              <option value="12MM">12MM</option>
                              <option value="20MM">20MM</option>
                              <option value="GSP">GSP</option>
                              <option value="WMM">WMM</option>
                            </select>
                          ) : modalSelectedOption === 'plant' && matchMat(formData.materialtype, 'cement') ? (
                            <select
                              name="grade"
                              value={formData.grade}
                              onChange={handleInputChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            >
                              <option value="">Select Grade</option>
                              <option value="OPC">OPC</option>
                              <option value="PPC">PPC</option>
                            </select>
                          ) : modalSelectedOption === 'plant' && matchMat(formData.materialtype, 'rmc') ? (
                            <select
                              name="grade"
                              value={formData.grade}
                              onChange={handleInputChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            >
                              <option value="">Select Grade</option>
                              <option value="M20">M20</option>
                              <option value="M25">M25</option>
                              <option value="M30">M30</option>
                              <option value="M35">M35</option>
                              <option value="M40">M40</option>
                            </select>
                          ) : modalSelectedOption === 'plant' && matchMat(formData.materialtype, 'bt') ? (
                            <select
                              name="grade"
                              value={formData.grade}
                              onChange={handleInputChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            >
                              <option value="">Select Grade</option>
                              <option value="BC">BC</option>
                              <option value="BM">BM</option>
                            </select>
                          ) : modalSelectedOption === 'site' && matchMat(formData.materialtype, 'steel') ? (
                            <select
                              name="grade"
                              value={formData.grade}
                              onChange={handleInputChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            >
                              <option value="">Select Grade</option>
                              <option value="6MM">6MM</option>
                              <option value="8">8</option>
                              <option value="12">12</option>
                              <option value="20">20</option>
                              <option value="25">25</option>
                              <option value="30">30</option>
                              <option value="Binding Wire">Binding Wire</option>
                            </select>
                          ) : modalSelectedOption === 'site' && matchMat(formData.materialtype, 'aggregate') ? (
                            <select
                              name="grade"
                              value={formData.grade}
                              onChange={handleInputChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            >
                              <option value="">Select Grade</option>
                              <option value="Chips">Chips</option>
                              <option value="6MM">6MM</option>
                              <option value="12MM">12MM</option>
                              <option value="20MM">20MM</option>
                              <option value="GSP">GSP</option>
                              <option value="Wetmix WMM">Wetmix WMM</option>
                            </select>
                          ) : modalSelectedOption === 'site' && matchMat(formData.materialtype, 'cement') ? (
                            <select
                              name="grade"
                              value={formData.grade}
                              onChange={handleInputChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            >
                              <option value="">Select Grade</option>
                              <option value="OPC">OPC</option>
                              <option value="PPC">PPC</option>
                            </select>
                          ) : modalSelectedOption === 'site' && matchMat(formData.materialtype, 'pvcpipes', 'wires') ? (
                            <input
                              type="text"
                              name="grade"
                              value={formData.grade}
                              onChange={handleInputChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                              placeholder="Enter grade"
                            />
                          ) : modalSelectedOption === 'site' && (formData.materialtype === 'frpmanualdoor' || formData.materialtype === 'frprwh') ? (
                            <select
                              name="grade"
                              value={formData.grade}
                              onChange={handleInputChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            >
                              <option value="">Select Grade</option>
                              <option value="600">600</option>
                              <option value="900">900</option>
                            </select>
                          ) : modalSelectedOption === 'site' && (formData.materialtype === 'frpcatchpit' || formData.materialtype === 'ci') ? (
                            <select
                              name="grade"
                              value={formData.grade}
                              onChange={handleInputChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            >
                              <option value="">Select Grade</option>
                              <option value="450x450">450x450</option>
                              <option value="600x600">600x600</option>
                            </select>
                          ) : modalSelectedOption === 'site' && matchMat(formData.materialtype, 'rmc') ? (
                            <input
                              type="text"
                              name="grade"
                              value={formData.grade}
                              onChange={handleInputChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                              placeholder="Enter grade"
                            />
                          ) : modalSelectedOption === 'site' && matchMat(formData.materialtype, 'bt') ? (
                            <select
                              name="grade"
                              value={formData.grade}
                              onChange={handleInputChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            >
                              <option value="">Select Grade</option>
                              <option value="BC">BC</option>
                              <option value="BM">BM</option>
                            </select>
                          ) : (
                            <input
                              type="text"
                              name="grade"
                              value={formData.grade}
                              onChange={handleInputChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            />
                          )}
                          </div>
                        );
                    })()}
                      </>
                    )}

                    {/* Select Weight dropdown — Plant mode */}
                    {(() => {
                      const _apiUnits = getApiUnits(formData.materialtype, 'plant');
                      const _apiHasTonnageCFT = _apiUnits.some((u) => matchMat(u, 'Tonnage', 'CFT'));
                      const _hardcoded = matchMat(formData.materialtype, 'MSAND', 'aggregate', 'cement', 'dust', 'bitman');
                      if (modalSelectedOption !== 'plant' || (!_hardcoded && !_apiHasTonnageCFT)) return null;
                      const _isCement = matchMat(formData.materialtype, 'cement');
                      const _isSteel = matchMat(formData.materialtype, 'steel');
                      // Build unit options: prefer API units, fall back to hardcoded
                      const _units = _apiUnits.length > 0
                        ? _apiUnits
                        : ['Tonnage', 'CFT', ...(_isCement ? ['Bag'] : [])];
                      return (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Select Weight Unit <span className="text-red-500">*</span>
                          </label>
                          <select
                            name="tonnageCFT"
                            value={formData.tonnageCFT}
                            onChange={handleInputChange}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          >
                            <option value="">Select</option>
                            {_units.map((u) => <option key={u} value={u}>{u}</option>)}
                          </select>
                        </div>
                      );
                    })()}

                    {/* Select Weight dropdown — Site mode */}
                    {(() => {
                      const _apiUnits = getApiUnits(formData.materialtype, 'site');
                      const _apiHasTonnageCFT = _apiUnits.some((u) => matchMat(u, 'Tonnage', 'CFT'));
                      const _hardcoded = matchMat(formData.materialtype, 'steel', 'aggregate', 'cement');
                      if (modalSelectedOption !== 'site' || (!_hardcoded && !_apiHasTonnageCFT)) return null;
                      const _isCement = matchMat(formData.materialtype, 'cement');
                      const _isSteel = matchMat(formData.materialtype, 'steel');
                      const _units = _apiUnits.length > 0
                        ? _apiUnits
                        : ['Tonnage', ...(!_isSteel ? ['CFT', ...(_isCement ? ['Bag'] : [])] : [])];
                      return (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Select Weight Unit <span className="text-red-500">*</span>
                          </label>
                          <select
                            name="tonnageCFT"
                            value={formData.tonnageCFT}
                            onChange={handleInputChange}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          >
                            <option value="">Select</option>
                            {_units.map((u) => <option key={u} value={u}>{u}</option>)}
                          </select>
                        </div>
                      );
                    })()}

                    {/* Select Unit Type dropdown — Site mode (PVC Pipes, Wires, or any API qty-unit material) */}
                    {(() => {
                      const _apiUnits = getApiUnits(formData.materialtype, 'site');
                      const _apiHasTonnageCFT = _apiUnits.some((u) => matchMat(u, 'Tonnage', 'CFT'));
                      const _apiHasQtyUnit = _apiUnits.length > 0 && !_apiHasTonnageCFT;
                      const _hardcoded = modalSelectedOption === 'site' && matchMat(formData.materialtype, 'pvcpipes', 'wires');
                      if (!_hardcoded && !_apiHasQtyUnit) return null;
                      const _isWires = matchMat(formData.materialtype, 'wires');
                      const _units = _apiHasQtyUnit
                        ? _apiUnits
                        : (_isWires ? ['Coils', 'Meters'] : ['Length', 'Meter']);
                      return (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Select Unit Type <span className="text-red-500">*</span>
                          </label>
                          <select
                            name="qtyUnit"
                            value={formData.qtyUnit}
                            onChange={handleInputChange}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          >
                            <option value="">Select Unit</option>
                            {_units.map((u) => <option key={u} value={u}>{u}</option>)}
                          </select>
                        </div>
                      );
                    })()}

                    {/* sq.mm input field for Wires - next to Unit Type */}
                    {(modalSelectedOption === 'site' && matchMat(formData.materialtype, 'wires')) && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          sq.mm
                        </label>
                        <input
                          type="text"
                          name="sqmm"
                          value={formData.sqmm}
                          onChange={handleInputChange}
                          onKeyDown={handleNumberInput}
                          placeholder="Enter sq.mm"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                      </div>
                    )}
                  </div>
                  </ModalSection>

                  {/* 4. Quantity & Amount */}
                  <ModalSection>
                    {(() => {
                      const _apiUnits = getApiUnits(formData.materialtype, 'plant');
                      const _apiHasTonnage = _apiUnits.some((u) => matchMat(u, 'Tonnage'));
                      const _hardcodedPlant = matchMat(formData.materialtype, 'MSAND', 'aggregate', 'cement', 'dust', 'bitman');
                      return modalSelectedOption === 'plant' && (_hardcodedPlant || _apiHasTonnage) && formData.tonnageCFT === 'Tonnage';
                    })() && (
                      <div className="grid grid-cols-4 gap-6 mb-4">
                        <div className='mb-2'>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Gross Weight <span className="text-red-500">*</span>
                          </label>
                          <input
                            type=""
                            name="grossWeight"
                            value={formData.grossWeight}
                            onChange={handleInputChange}
                            onKeyDown={handleNumberInput}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          />
                        </div>
                        <div className=''>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Tare Weight <span className="text-red-500">*</span>
                          </label>
                          <input
                            type=""
                            name="tareWeight"
                            value={formData.tareWeight}
                            onChange={handleInputChange}
                            onKeyDown={handleNumberInput}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          />
                        </div>
                        <div className=''>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Net Weight 
                          </label>
                          <input
                            type=""
                            name="netWeight"
                            value={formData.netWeight}
                            onChange={handleInputChange}
                            onKeyDown={handleNumberInput}
                            required
                            readOnly
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-gray-100 text-gray-600 cursor-not-allowed"
                          />
                        </div>
                        <div className=''>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Total Amount (₹) <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="number"
                            name="totalamount"
                            value={formData.totalamount}
                            onChange={handleInputChange}
                            onKeyDown={handleNumberInput}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          />
                          <AmountWithGstHint gstPercentage={formData.gstPercentage} withGST={formData.withGST} />
                        </div>
                      </div>
                    )}

   {/* Rate - Show for Plant mode when material type is NOT a weight/tonnage material */}
                  {modalSelectedOption === 'plant' && formData.materialtype &&
                   !matchMat(formData.materialtype, 'MSAND', 'aggregate', 'cement', 'dust', 'bitman') &&
                   !getApiUnits(formData.materialtype, 'plant').some((u) => matchMat(u, 'Tonnage', 'CFT')) && (
                    <div className="grid grid-cols-4 gap-6 mb-4">
                      <div className=''>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Rate (₹) <span className="text-red-500">*</span>
                        </label>
                        <input
                          type=""
                          name="rate"
                          value={formData.rate}
                          onChange={handleInputChange}
                          onKeyDown={handleNumberInput}
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                      </div>
                
                     
                       {/* Quantity - only for Plant mode (Site mode has its own row) */}
                    {formData.materialtype && modalSelectedOption === 'plant' &&
                     !matchMat(formData.materialtype, 'MSAND', 'aggregate', 'cement', 'dust', 'bitman') &&
                     !getApiUnits(formData.materialtype, 'plant').some((u) => matchMat(u, 'Tonnage', 'CFT')) && (
                      <div className=''>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {formData.materialtype === 'others' ? 'Qty (unit)' :
                           matchMat(formData.materialtype, 'rmc') ? 'Qty (metric tons)' :
                           matchMat(formData.materialtype, 'ldooil', 'ldo oil', 'diesel') ? 'Qty (ltrs)' :
                           matchMat(formData.materialtype, 'bt') ? 'Qty (unit)' : 'Qty (unit)'} <span className="text-red-500">*</span>
                        </label>
                        <input  
                          type=""
                          name="qty"
                          value={formData.qty}
                          onChange={handleInputChange}
                          onKeyDown={handleNumberInput}
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                      </div>
                    )}

                         <div className=''>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {matchMat(formData.materialtype, 'ldooil', 'ldo oil', 'diesel') ? 'Total Amount (Rate x Qty)' : 'Total Amount (₹)'}
                        </label>
                        <input
                          type=""
                          name="totalamount"
                          value={formData.totalamount}
                          onChange={handleInputChange}
                          onKeyDown={handleNumberInput}
                          required
                          readOnly={matchMat(formData.materialtype, 'ldooil', 'ldo oil', 'diesel')}
                          className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                            matchMat(formData.materialtype, 'ldooil', 'ldo oil', 'diesel') ? 'bg-gray-100 text-gray-600 cursor-not-allowed' : ''
                          }`}
                        />
                        <AmountWithGstHint gstPercentage={formData.gstPercentage} withGST={formData.withGST} />
                      </div>
                      </div>
                    )}

                    {/* Rate, Quantity, and Total Amount fields for MSAND, Aggregate, Cement, Dust, and Bitumen Thar when CFT is selected */}
                    {modalSelectedOption === 'plant' && isPlantCftStyleWeight(formData) && (
                       <div className="grid grid-cols-4 gap-6 mb-4">
                        <div className=''>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Rate (₹) <span className="text-red-500">*</span>
                          </label>
                          <input
                            type=""
                            name="rate"
                            value={formData.rate}
                            onChange={handleInputChange}
                            onKeyDown={handleNumberInput}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          />
                        </div>
                        <div className=''>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Quantity <span className="text-red-500">*</span>
                          </label>
                          <input
                            type=""
                            name="quantity"
                            value={formData.quantity}
                            onChange={handleInputChange}
                            onKeyDown={handleNumberInput}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          />
                        </div>
                        <div className=''>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Total Amount (₹) <span className="text-red-500">*</span>
                          </label>
                          <input
                            type=""
                            name="cftTotalAmount"
                            value={formData.cftTotalAmount}
                            onChange={handleInputChange}
                            onKeyDown={handleNumberInput}
                            required
                            readOnly
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-gray-100 text-gray-600 cursor-not-allowed"
                          />
                          <AmountWithGstHint gstPercentage={formData.gstPercentage} withGST={formData.withGST} />
                        </div>
                      </div>
                    )}
                

                    {/* Weight fields for Steel, Aggregate, and Cement when Tonnage is selected in Site mode */}
                    {modalSelectedOption === 'site' && (matchMat(formData.materialtype, 'steel', 'aggregate', 'cement') || getApiUnits(formData.materialtype, 'site').some((u) => matchMat(u, 'Tonnage'))) && formData.tonnageCFT === 'Tonnage' && (
                      <div className="grid grid-cols-4 gap-6 mb-4">
                        <div className=''>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Gross Weight <span className="text-red-500">*</span>
                          </label>
                          <input
                            type=""
                            name="grossWeight"
                            value={formData.grossWeight}
                            onChange={handleInputChange}
                            onKeyDown={handleNumberInput}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          />
                        </div>
                        <div className=''>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Tare Weight <span className="text-red-500">*</span>
                          </label>
                          <input
                            type=""
                            name="tareWeight"
                            value={formData.tareWeight}
                            onChange={handleInputChange}
                            onKeyDown={handleNumberInput}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          />
                        </div>
                        <div className=''>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Net Weight 
                          </label>
                          <input
                            type=""
                            name="netWeight"
                            value={formData.netWeight}
                            onChange={handleInputChange}
                            onKeyDown={handleNumberInput}
                            required
                            readOnly
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-gray-100 text-gray-600 cursor-not-allowed"
                          />
                        </div>
                        <div className=''>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Total Amount (₹) <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="number"
                            name="totalamount"
                            value={formData.totalamount}
                            onChange={handleInputChange}
                            onKeyDown={handleNumberInput}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          />
                          <AmountWithGstHint gstPercentage={formData.gstPercentage} withGST={formData.withGST} />
                        </div>
                      </div>
                    )}

                    {/* Rate, Quantity, and Total Amount fields for Aggregate, and Cement when CFT is selected in Site mode */}
                    {modalSelectedOption === 'site' && isSiteCftStyleWeight(formData) && (
                      <div className="grid grid-cols-4 gap-6 mb-4">
                        <div className=''>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Rate (₹) <span className="text-red-500">*</span>
                          </label>
                          <input
                            type=""
                            name="rate"
                            value={formData.rate}
                            onChange={handleInputChange}
                            onKeyDown={handleNumberInput}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          />
                        </div>
                        <div className=''>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Quantity <span className="text-red-500">*</span>
                          </label>
                          <input
                            type=""
                            name="quantity"
                            value={formData.quantity} 
                            onChange={handleInputChange}
                            onKeyDown={handleNumberInput}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          />
                        </div>
                        <div className=''>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Total Amount (₹) <span className="text-red-500">*</span>
                          </label>
                          <input
                            type=""
                            name="cftTotalAmount"
                            value={formData.cftTotalAmount}
                            onChange={handleInputChange}
                            onKeyDown={handleNumberInput}
                            required
                            readOnly
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-gray-100 text-gray-600 cursor-not-allowed"
                          />
                          <AmountWithGstHint gstPercentage={formData.gstPercentage} withGST={formData.withGST} />
                        </div>
                      </div>
                    )}

                    {/* Rate, Quantity, and Total Amount row for Site mode - exclude Steel, Aggregate, and Cement when they have tonnageCFT selected */}
                    {modalSelectedOption === 'site' && formData.materialtype &&
                     !((matchMat(formData.materialtype, 'steel') && formData.tonnageCFT === 'Tonnage') || ((matchMat(formData.materialtype, 'aggregate', 'cement') || getApiUnits(formData.materialtype, 'site').some((u) => matchMat(u, 'Tonnage', 'CFT'))) && formData.tonnageCFT)) && (
                      <div className="grid grid-cols-4 gap-6 mb-4">
                        <div className=''>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Rate (₹) <span className="text-red-500">*</span>
                          </label>
                          <input
                            type=""
                            name="rate"
                            value={formData.rate}
                            onChange={handleInputChange}
                            onKeyDown={handleNumberInput}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          />
                        </div>

                        <div className=''>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {formData.materialtype === 'others' ? 'Qty (unit)' :
                             matchMat(formData.materialtype, 'steel', 'aggregate') ? 'Qty (metric tons)' :
                             matchMat(formData.materialtype, 'pvcpipes') ? (formData.qtyUnit ? `Qty (${formData.qtyUnit})` : 'Qty (Length/Meter)') :
                             matchMat(formData.materialtype, 'wires') ? (formData.qtyUnit ? `Qty (${formData.qtyUnit})` : 'Qty (Coils/Meters)') :
                             matchMat(formData.materialtype, 'frpmanualdoor') ? 'Quantity' :
                             matchMat(formData.materialtype, 'frpcatchpit', 'ci', 'frprwh') ? 'Qty (MM)' :
                             matchMat(formData.materialtype, 'rmc') ? 'Qty (unit)' :
                             matchMat(formData.materialtype, 'bt') ? 'Qty (unit)' : 'Qty (unit)'} <span className="text-red-500">*</span>
                          </label>
                          <input
                            type=""
                            name="qty"
                            value={formData.qty}
                            onChange={handleInputChange}
                            onKeyDown={handleNumberInput}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          />
                        </div>

                        <div className=''>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Total Amount (₹) <span className="text-red-500">*</span>
                          </label>
                          <input
                            type=""
                            name="totalamount"
                            value={formData.totalamount}
                            onChange={handleInputChange}
                            onKeyDown={handleNumberInput}
                            required
                            readOnly
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-gray-100 text-gray-600 cursor-not-allowed"
                          />
                          <AmountWithGstHint gstPercentage={formData.gstPercentage} withGST={formData.withGST} />
                        </div>
                      </div>
                    )}

                  </ModalSection>

                  {/* 5. Additional Details */}
                  <ModalSection>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Vendor <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="vendor"
                          value={formData.vendor}
                          onChange={handleInputChange}
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        >
                          <option value="">Select Vendor</option>
                          {modalVendorOptions.map((vendor) => (
                            <option key={vendor} value={vendor}>
                              {vendor}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Supervisor <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="supervisor"
                          value={formData.supervisor}
                          onChange={handleInputChange}
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        >
                          <option value="">Select Supervisor</option>
                          {supervisorsFromApi.length > 0
                            ? supervisorsFromApi.map((sup) => (
                                <option key={sup.value} value={sup.value}>
                                  {sup.label}
                                </option>
                              ))
                            : supervisorOptions
                                .filter((opt) => opt.value !== 'all')
                                .map((opt) => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                        </select>
                      </div>
                    </div>
                  </ModalSection>
                </div>

                {/* Footer */}
                <div className="flex flex-shrink-0 justify-end space-x-3 border-t border-gray-200 bg-gray-50 px-6 py-3">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                    style={{ backgroundColor: '#009688' }}
                  >
                    {isEditMode ? 'Update' : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Background overlay */}
          <div 
            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            onClick={handleCancelDelete}
          ></div>

          {/* Confirmation Dialog */}
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 transform transition-all">
            <div className="p-6">
              {/* Warning Icon */}
              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full">
                <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              
              {/* Message */}
              <h3 className="text-lg font-medium text-gray-900 text-center mb-2">
                Confirm Delete
              </h3>
              <p className="text-sm text-gray-500 text-center mb-6">
                Are you sure you want to delete this record? This action cannot be undone.
              </p>
              
              {/* Action Buttons */}
              <div className="flex gap-2 justify-center">
                <button
                  type="button"
                  onClick={handleCancelDelete}
                  className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmDelete}
                  className="px-6 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 hover:opacity-90"
                  style={{ backgroundColor: '#dc2626' }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default StockIn;

